/**
 * FlightPlanTab — the daily board for the President and Studio Director (2026-08-26).
 *
 * Replaces the old "Today" exception stream. Today answered "what is broken"; this answers
 * "what is in the air, what is queued, and who is flying it."
 *
 * A milestone put into execution becomes a FLIGHT with a real flight number (CFW101, IDP104)
 * crewed with team members. This is a departures board: flight number, destination, scheduled
 * time, crew, status.
 *
 * Structure: a HILL CHART at the top (FlightTracker) — the waiting queue lives at its own gate,
 * as a static vertical stack of icons (see FlightTracker's own doc comment, "FlightDeck"/
 * "ThreadDeck") — then two lists below it: "In the air" (real flights and judged threads, each
 * with its own checklist/actions) and "Next up" (the same queue the gate stacks show, as
 * readable rows). The hill position is judged by a human and carries a required note; the
 * checklist percentage sits next to it precisely so the two can be seen to disagree.
 *
 * All data through services/flights.js. No direct API calls here.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { getFlightPlan, createFlight, setFlightCrew, landFlight, setFlightHill, removeThreadFromSquadron, createSquadron, addThreadToSquadron, crewThread, getCrewRoster, eligibleCrew, checkLaunch } from '../services/flights.js'
import { getPassengersFor } from '../services/passengers.js'
import GateCheckModal from './GateCheckModal.jsx'
import { getThreadHillPlan, setThreadHill, landThread } from '../services/threadHill.js'
import { fastTrackThread } from '../services/threads.js'
import { wf } from '../outline/state.js'
import { getActiveProjects } from '../services/commands.js'
import { getTeamMembersWithRoles } from '../services/members.js'
import { getRoleSkills } from '../services/teamOverview.js'
import { requiredSkills, personSkills } from '../outline/eligibility.js'
import { getMilestoneChecklistItems, completeChecklistItem } from '../services/checklist.js'
import { HIDDEN_CL_CATEGORIES } from '../outline/constants.js'
import { ActionMenu, ActionMenuItem, ActionMenuDivider, useActionMenu } from './ActionMenu.jsx'
import { confirmDialog, promptDialog, alertDialog } from './Dialog.jsx'
import { PALETTE } from './MiniChart.jsx'
import useLiveRefresh from '../lib/useLiveRefresh.js'
import { EntityPeekModal } from './EntityPeek.jsx'
import FlightTracker, { attitudeFor, threadAttitudeFor, flightTag, threadTag, Plane, fuelTone, isSeedThread, TAKEOFF_T } from './FlightTracker.jsx'

/**
 * FULL KIT at takeoff (docs/FLOW_RECONCILIATION_PLAN.md Step 2). The server checks the kit live
 * when a milestone is put in the air; if the predecessor hasn't posted or Full-Kit items are still
 * open it hands the kit back instead of launching, and the tower is asked for a one-line reason.
 * The reason is recorded on the flight and the plane carries an open-kit badge (the low-fuel
 * pattern) until the kit is complete. Cancelling the prompt launches nothing.
 */
async function launchWithKitCheck(m, members, hill) {
  const first = await createFlight(m.milestoneId, members, hill)
  if (first && first.refused) { await showRefusal(first.refused); return { cancelled: true } }
  if (!first || !first.kitRequired) return first
  const k = first.kit || {}
  const missing = []
  if (k.predecessorPosted === false) missing.push('its predecessor has not posted yet')
  // The Full-Kit checklist category no longer counts (2026-09-10); the GATE is the full kit and is
  // checked interactively before the crew picker (askThenCrew → GateCheckModal). Kept for shape.
  if (k.preworkOpen > 0) missing.push(`${k.preworkOpen} checklist item${k.preworkOpen === 1 ? ' is' : 's are'} still open`)
  const note = await promptDialog({
    title: 'Launch without full kit?',
    message: `${m.name}: ${missing.join(', and ')}. You can put it in the air anyway — say why. The reason is kept on the flight, and the plane will carry an open-kit marker until the full kit is complete.`,
    placeholder: 'Why now, in a line',
    confirmLabel: 'Put in the air anyway',
    required: true,
    multiline: true,
  })
  if (note === null) return { cancelled: true }
  const second = await createFlight(m.milestoneId, members, hill, note)
  if (second && second.refused) { await showRefusal(second.refused); return { cancelled: true } }
  return second
}

/**
 * THE ROPE (sheet 2, database/229). A refusal is shown, never swallowed: the gate is still open,
 * the leg is full, or the crew has no free slot. The server wrote it down already.
 */
// One shared empty list: an absent collection must never read as "changed" to a memo or effect.
const EMPTY = []

// The open items read as a list, one per line (Matt, 2026-10-22: a paragraph of item names was awkward).
function showRefusal(r) {
  const d = r.detail || {}
  const items = Array.isArray(d.open) ? d.open.filter(Boolean) : []
  if (!items.length || (r.kind !== 'gate' && r.kind !== 'predecessor')) return alertDialog({ title: r.title || 'Not cleared for takeoff', message: r.message || '' })
  const lead = r.kind === 'gate'
    ? `${items.length} of ${d.total || items.length} gate item${items.length === 1 ? ' is' : 's are'} still open:`
    : `It is waiting on ${items.length === 1 ? 'this milestone' : 'these milestones'} to post:`
  const after = r.kind === 'gate' ? 'Tick each one \u201cIn hand\u201d, or mark it \u201cNot required\u201d with a reason, and launch again.' : 'Post ' + (items.length === 1 ? 'it' : 'them') + ' first, then launch again.'
  return alertDialog({
    title: r.title || 'Not cleared for takeoff',
    message: (
      <>
        <div>{lead}</div>
        <ul style={{ margin: '6px 0 8px', paddingLeft: 18 }}>{items.map((x, i) => <li key={i} style={{ marginBottom: 2 }}>{x}</li>)}</ul>
        <div>{after}</div>
      </>
    ),
  })
}

/**
 * Ask before the crew picker opens. Picking two people and THEN being told the leg is full is
 * the wrong order; this puts the answer first. The real launch checks again with the crew.
 */
async function askThenCrew(m, hill, setCrewing, setError, setGateCheck) {
  try {
    const r = await checkLaunch(m.milestoneId)
    if (r && r.refused) {
      // An open GATE is not a dead end: the full kit opens as a live list the tower can work through
      // right here (Matt, 2026-09-10 — GateCheckModal). Leg-full and crew-slot refusals stay refusals.
      if (r.refused.kind === 'gate' && setGateCheck) { setGateCheck({ m, hill }); return }
      await showRefusal(r.refused); return
    }
  } catch (e) { setError && setError(String(e && e.message || e)); return }
  setCrewing(hill == null ? { milestone: m } : { milestone: m, hill })
}

/**
 * LaunchMilestone — put one milestone in the air from outside this tab (the Line-up's "Crew it",
 * 2026-09-27). The SAME path In the Air uses: the launch check first (a gate that is still open opens
 * GateCheckModal to work through; a leg-full / crew-slot refusal is shown), then the crew picker, then
 * launchWithKitCheck. It loads what the picker needs itself (the board's crew loads, the team, the crew
 * roster and role skills), so the caller passes only the milestone and hears back when it is done.
 *   milestone: { milestoneId, name, projectName, latestFinish?, earliestStart? }
 *   onDone(result | null) — result is { flight } on a launch; null when cancelled or refused.
 */
export function LaunchMilestone({ milestone, onDone }) {
  const [stage, setStage] = useState('checking')   // checking | gate | crew
  const [ctxData, setCtxData] = useState(null)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await checkLaunch(milestone.milestoneId)
        if (cancelled) return
        if (r && r.refused) {
          if (r.refused.kind === 'gate') { setStage('gate'); return }
          await showRefusal(r.refused); onDone(null); return
        }
        const [plan, team, roster, roleSkills] = await Promise.all([
          getFlightPlan(), getTeamMembersWithRoles().catch(() => []), getCrewRoster().catch(() => []), getRoleSkills().catch(() => null),
        ])
        if (cancelled) return
        setCtxData({ plan, team, roster, roleSkills }); setStage('crew')
      } catch (e) {
        if (cancelled) return
        await alertDialog({ title: 'Could not start the launch', message: String(e && e.message || e) })
        onDone(null)
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage === 'checking' ? milestone.milestoneId : null])

  if (stage === 'gate') {
    return <GateCheckModal milestoneId={milestone.milestoneId} name={milestone.name} projectName={milestone.projectName}
      onClose={() => onDone(null)} onCleared={() => setStage('checking')} />
  }
  if (stage !== 'crew' || !ctxData) return null
  const { plan, team, roster, roleSkills } = ctxData
  const flights = (plan && plan.flights) || []
  return (
    <CrewPicker
      target={{ milestone }} team={eligibleCrew(team, roster)} roleSkills={roleSkills} crewLoad={(plan && plan.crewLoad) || {}}
      maxCrew={(plan && plan.maxCrew) || 2}
      flightById={new Map(flights.map(f => [f.flightId, f]))} threadById={new Map()}
      onClose={() => onDone(null)}
      onSave={async (members) => {
        try {
          const result = await launchWithKitCheck(milestone, members, null)
          onDone(result && !result.cancelled && result.flight ? result : null)
        } catch (e) { await alertDialog({ title: 'That did not launch', message: String(e && e.message || e) }); onDone(null) }
      }} />
  )
}

/** "Sara (1st)" — a crew member's name with the rank this plane holds for THEM (2026-09-10). */
export function crewName(c) {
  if (!c) return ''
  return c.name + (c.priority === 1 ? ' (1st)' : c.priority === 2 ? ' (2nd)' : '')
}

/** Plain-English reading of a hill position, used in the move prompt and on each flight card. */
export function hillLabel(t) {
  if (t < 0.18) return 'Wheels up'
  if (t < 0.42) return 'Plotting the course'
  if (t < 0.58) return 'Course set — we know how to do this'
  if (t < 0.85) return 'Flying the course'
  return 'On final approach'
}

// Matt: "The top bar with numbers of different categories is not proving to be that helpful to
// me and maybe just noise. Let's hide it for now and we can bring it back later if we want" —
// a flag, not a deletion, so turning it back on later is a one-line change rather than
// reconstructing the whole stats row from git history. The Refresh control stays visible either
// way (see its own flex spacer below) — hiding the counts isn't the same as removing the refresh.
const SHOW_STATS_BAR = false

const DAY = 86400000

const toDate = (s) => (s ? new Date(s + 'T00:00:00') : null)
const fmtDay = (s) => {
  const d = toDate(s)
  return d ? d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : '—'
}
/** Days budgeted arrives as a raw float — "2.600894354387926d" is not a number anyone reads. */
const fmtDays = (n) => `${Math.round(Number(n) * 10) / 10}d`


export default function FlightPlanTab() {
  // Clicking a milestone or thread — in the list or on the curve — used to navigate the whole
  // tab away (or, from the explicit "Open ↗" menu items, pop a new browser tab). Matt: "I want
  // it to open a modal with an iframe that jumps straight to its position in the outline,
  // instead of opening it in a new tab... Do not recreate the outline. Use what we already
  // have." `peek` is the one state that drives that modal — see EntityPeekModal below.
  const [peek, setPeek] = useState(null)   // { type: 'milestone'|'thread', entityId, projectId }
  const [gateCheck, setGateCheck] = useState(null)   // { m, hill } — the interactive full kit before the crew picker (GateCheckModal)

  const openMs = useCallback((m) => {
    if (!m || !m.milestoneId) return
    setPeek({ type: 'milestone', entityId: m.milestoneId, projectId: m.projectId, projectName: m.projectName || null })
  }, [])

  const openThread = useCallback((th) => {
    if (!th || !th.threadId) return
    setPeek({ type: 'thread', entityId: th.threadId, projectId: th.projectId, projectName: th.projectName || null })
  }, [])

  // Mirrors FlightTracker's own gate stacks (lineItems/queuedThreads) so "Next up" below can
  // render them as an actual readable list — Matt: "the list at the top [In the air] to show
  // those in flight and then under that list another list showing the next ones coming up for
  // flight." One computation (inside FlightTracker, which already has the scope/schedule-window
  // logic), reported outward via onQueueChange rather than duplicated here.
  const [queue, setQueue] = useState({ flights: [], threads: [], hidden: [] })

  const [plan, setPlan] = useState(null)
  const [threadPlan, setThreadPlan] = useState(null)   // the threads curve's own board — see services/threadHill.js
  const [team, setTeam] = useState([])
  // Crew roster exceptions (decision 8): who on the team is NOT crew. The picker only ever shows
  // eligibleCrew(team, roster); the roster itself is edited in Admin → Crew (CrewRosterPanel.jsx).
  const [roster, setRoster] = useState([])
  const [roleSkills, setRoleSkills] = useState(null)   // role → expected skills, for crew skill fit
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  const [crewing, setCrewing] = useState(null)   // { milestone } | { flight }
  const [picking, setPicking] = useState(null)   // a project whose milestones are being browsed
  // Hovering a row in "In the air" below (2026-09-03) — a flightId or threadId, passed to
  // FlightTracker as `hoverRowId` so the matching on-curve icon grows and glows. See
  // FlightTracker.jsx's own `glow` comment for why this is separate from its internal hover.
  const [rowHoverId, setRowHoverId] = useState(null)
  // { [milestoneId]: { items, done, total } }. ONE live fetch per flight, shared by the flight
  // card's checklist body AND the hill hover card. Two independent reads of "is it done" —
  // one live, one off the Supabase mirror's completed/incomplete_checklist_items columns —
  // is exactly the staleness trap already hit once with posted status: DD101 showed "0 of 0
  // ticked" on hover while its own card read 1/74, because the mirror hadn't caught up.
  const [checklists, setChecklists] = useState({})
  const loadedRef = useRef(false)
  const [landingFirst, setLandingFirst] = useState(() => { try { return localStorage.getItem('aj-fp-landing-first') !== '0' } catch { return true } })
  const holdPendingRef = useRef(false)
  // Set the moment a flight is dragged all the way to the right on the curve and the resulting
  // PostMilestoneModal is opened (see FlightTracker.jsx's own drag handler) — a successful post
  // there fires 'outline-render' just like every other post path, so this reloads the board the
  // same way grounding a project already does, rather than leaving a landed flight showing as
  // still in the air until someone clicks Refresh.
  const postPendingRef = useRef(false)
  // Same idea, for a thread dragged to the far right — ThreadActivityModal (preset to close it)
  // fires 'outline-render' on a successful close, same as every other thread-activity save.
  const threadClosePendingRef = useRef(false)
  const clAskedRef = useRef(new Set())

  const dirRef = useRef(null)   // the project names, read once: the live re-read reuses them (Airtable)
  // silent: the live re-read (useLiveRefresh below) — no "Refreshing…" and no cleared error banner.
  const load = useCallback(async (silent) => {
    if (silent !== true) { setLoading(true); setError('') }
    try {
      let dir = silent === true ? dirRef.current : null
      if (!dir) try {
        const projs = await getActiveProjects()
        dir = {}
        for (const p of (projs || [])) {
          const f = p.fields || {}
          // Name only, no project-number prefix — every other project shown on this board
          // (flight cards, hover cards, the scope picker) reads its name straight off the
          // flight-plan/thread-hill data, which never includes the number. A project that only
          // ever gets its name from THIS backfill (no active milestone named it) stood out with
          // a number stuck on the front where its siblings had none.
          dir[p.id] = f['Project Name'] || ''
        }
      } catch { /* the board is still useful without it */ }
      dirRef.current = dir
      const [flightPlan, threads] = await Promise.all([
        getFlightPlan(dir),
        // The threads curve is a second, independent board — a failure here should never take
        // the flights half down with it.
        // Threads became PASSENGERS on 2026-09-09 (docs/PASSENGERS.md): they ride milestones and no
        // longer have a curve, a deck, squadrons, or crews of their own. The thread board is not
        // loaded; every thread-side render path below stays in the file but sees an empty list.
        Promise.resolve(null),
      ])
      setPlan(flightPlan)
      setThreadPlan(threads)
    } catch (e) {
      if (silent !== true) setError(String(e && e.message || e))
    } finally {
      if (silent !== true) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    load()
    getTeamMembersWithRoles().then(setTeam).catch(() => {})
    getCrewRoster().then(setRoster).catch(() => {})
    // Role → expected skills. Best-effort: the picker ranks by fit when this lands and stays a
    // plain alphabetical roster when it does not.
    getRoleSkills().then(setRoleSkills).catch(() => {})
  }, [load])

  // Fetch each flying milestone's checklist once, the moment it appears on the board — not on
  // hover and not on row-open, so the hover card is never a beat behind the flight card below it.
  const fetchChecklist = useCallback((msId) =>
      getMilestoneChecklistItems(msId)
        .then(rows => {
          const items = (rows || []).map(r => {
            const x = r.fields || {}
            return {
              id: r.id,
              name: first(x['Name for List (from Deliverable Checklist Item Template)']) || 'Item',
              cat: first(x['Category (from Deliverable Checklist Item Template)']) || 'Other',
              done: x['Complete'] === true,
              skip: x['Milestone Not Required'] === true,
            }
          })
          const relevant = items.filter(i => !i.skip)
          setChecklists(c => ({ ...c, [msId]: { items, done: relevant.filter(i => i.done).length, total: relevant.length } }))
        })
        .catch(() => setChecklists(c => (c[msId] ? c : { ...c, [msId]: { items: [], done: 0, total: 0 } }))), [])
  useEffect(() => {
    for (const f of (plan && plan.flights) || []) {
      if (!f.milestoneId || clAskedRef.current.has(f.milestoneId)) continue
      clAskedRef.current.add(f.milestoneId)
      fetchChecklist(f.milestoneId)
    }
  }, [plan, fetchChecklist])

  // LIVE (Matt, 2026-10-27: "updates in real time ... every frame on that tab"): the board, the hill and
  // each flying milestone's checklist re-read quietly every 20 seconds while this tab is in view, at once
  // after a crew change anywhere in this browser, and when the tab comes back into view. Waits while a
  // picker, dialog or save is open, or while something is being dragged (lib/useLiveRefresh.js).
  const planRef = useRef(plan); planRef.current = plan
  const pausedRef = useRef(false)
  pausedRef.current = !!(busy || crewing || picking || gateCheck || peek)
  const tickRef = useRef(0)
  useLiveRefresh(async () => {
    await load(true)
    // Checklists are one Airtable read per flight: every third beat (once a minute) is plenty.
    if (tickRef.current++ % 3 !== 2) return
    for (const f of (planRef.current && planRef.current.flights) || []) if (f.milestoneId) fetchChecklist(f.milestoneId)
  }, {
    everyMs: 20000,
    events: ['crew-index-changed', 'build-ready-changed'],
    pause: () => pausedRef.current || !!document.querySelector('[role="dialog"]'),
  })

  /**
   * Grounding reuses the existing Project Hold modal rather than duplicating it — that flow
   * snapshots the prior Contract Stage, restores it on resume, and fires the client email, the
   * team Slack post and the 30-day nudge. A second half-implementation here would skip all of
   * that. It renders at z-9000, above this workspace's z-400.
   */
  const openHold = useCallback((p) => {
    if (!p.projectId) return
    holdPendingRef.current = true
    window.dispatchEvent(new CustomEvent('open-project-hold-modal', { detail: { projId: p.projectId } }))
  }, [])

  // The hold modal, the post-milestone modal (a flight dragged to the far right — CloseoutModal
  // hands off to it internally, so this same event fires whether the drag opened the closeout
  // wizard or the outline's own menu opened the post modal directly), and the thread-activity
  // modal (a thread dragged to the far right, preset to close) all signal success with
  // 'outline-render'. Only reload when we were the one who opened one of them, since that event
  // fires constantly for unrelated reasons.
  useEffect(() => {
    const onOpenPost = () => { postPendingRef.current = true }
    const onOpenThreadActivity = () => { threadClosePendingRef.current = true }
    const onRender = () => {
      if (!holdPendingRef.current && !postPendingRef.current && !threadClosePendingRef.current) return
      holdPendingRef.current = false
      postPendingRef.current = false
      threadClosePendingRef.current = false
      load()
    }
    window.addEventListener('open-post-ms-modal', onOpenPost)
    window.addEventListener('open-thread-activity-modal', onOpenThreadActivity)
    window.addEventListener('outline-render', onRender)
    return () => {
      window.removeEventListener('open-post-ms-modal', onOpenPost)
      window.removeEventListener('open-thread-activity-modal', onOpenThreadActivity)
      window.removeEventListener('outline-render', onRender)
    }
  }, [load])

  // Every mutation used to await the FULL board reload (getFlightPlan — crew, flights, milestones,
  // legs, priority scoring, the works) before anything on screen changed, which is exactly the lag
  // Matt reported (2026-09-10): "a lot of delay ... between making a change and seeing it
  // reflected." The technique is optimistic UI: when a caller already knows the real outcome of its
  // own write (the hill position it just dropped at, the crew list it just picked, the flight row
  // the server just handed back), it passes a `patch(result, prevPlan) => nextPlan` — applied to
  // `plan` the INSTANT the write succeeds, using real data, never a guess. `load()` still runs
  // afterward to reconcile everything a patch can't know (fuel/kit bands, legs, capacity
  // elsewhere) but is fired, not awaited, so the interaction is already visually done by the time
  // it lands. Callers with no patch (the rarer, lower-traffic actions) keep the old wait-for-reload
  // behavior by simply omitting the third argument.
  const act = useCallback(async (key, fn, patch) => {
    setBusy(key); setError('')
    try {
      const result = await fn()
      if (patch) setPlan(p => (p ? patch(result, p) : p))
      else await load()
    }
    catch (e) { setError(String(e && e.message || e)); setBusy(''); return }
    setBusy('')
    if (patch) load()   // background reconcile — not awaited
  }, [load])

  // Shared by every crew-write patch below — rebuilds `plan.crewLoad`'s per-member assignment
  // list for one flight from data we already have, mirroring the exact shape api/flights.js's own
  // `flightPlan()` builds it in (`load[memberId].push({name, flightId, flightNumber, projectName,
  // priority})`). Keeps the Pilots list and the "N flying / N available" header stat correct
  // immediately, not just the flight card's own crew line.
  function patchCrewLoadForFlight(crewLoad, flightId, oldMemberIds, newMembers, flightMeta) {
    const next = { ...(crewLoad || {}) }
    for (const mid of oldMemberIds) {
      if (next[mid] && !newMembers.some(m => m.memberAirtableId === mid)) {
        next[mid] = next[mid].filter(a => a.flightId !== flightId)
      }
    }
    for (const m of newMembers) {
      const existing = (next[m.memberAirtableId] || []).filter(a => a.flightId !== flightId)
      next[m.memberAirtableId] = [...existing, {
        name: m.memberName, flightId, flightNumber: flightMeta.flightNumber,
        projectName: flightMeta.projectName, priority: m.priority || null,
      }]
    }
    return next
  }

  const doLand = useCallback(async (f, reason) => {
    // "Mark landed" used to call landFlight(f.flightId, 'posted') directly — which ONLY flips
    // the flight row to landed/posted and frees the crew in Supabase. It never touched the
    // actual milestone: no Posted Date, no checklist gate, nothing in Airtable actually changed.
    // A flight could show "landed" while its milestone sat open, unposted, checklist untouched.
    // Route through the exact same closeout FlightTracker's own drag-to-the-far-right gesture
    // already uses (CloseoutModal → the real post flow) instead of a second, hollow path — a
    // successful post there lands the flight for real via the ordinary posting-is-landing
    // reconciliation on the next board load, crew release included.
    if (reason === 'posted') {
      window.dispatchEvent(new CustomEvent('open-closeout-modal', {
        detail: { msId: f.milestoneId, name: f.name, projectId: f.projectId },
      }))
      return
    }
    const label = reason === 'cancelled' ? 'Cancel flight' : 'Pull from the air'
    if (!(await confirmDialog({
      title: label + '?',
      message: `${label} — ${flightTag(f)}, ${f.name}${f.projectName ? ` on ${f.projectName}` : ''}?`,
      confirmLabel: label, danger: reason === 'cancelled',
    }))) return
    // The flight is gone the instant the write succeeds — no need to wait for the reload to
    // learn what we already told the server to do.
    act(f.flightId, () => landFlight(f.flightId, reason), (_, p) => ({
      ...p, flights: p.flights.filter(x => x.flightId !== f.flightId),
    }))
  }, [act])

  // FAST TRACK a single thread to landing (2026-08-28) — the administrative close for work
  // already done (or dead) in reality: open subtasks completed through the real per-item path,
  // then the ordinary Thread Closed activity + Closed checkbox, stamped "Fast-tracked" so it
  // never reads as an organically-worked close in later metrics. The board reload lands it via
  // the standard closing-is-landing reconciliation. Bulk version: bulkFastTrack, the "Next up"
  // multi-select toolbar below.
  // `opts.viaDrag` skips the confirm: dragging a queued thread all the way to the landing zone
  // is already a deliberate gesture with its own green "Release to close" preview — a dialog
  // right after deflates it (Matt: "Dragging it over just feels like an accomplishment").
  const doFastTrackThread = useCallback(async (th, opts) => {
    if (!(opts && opts.viaDrag)) {
      if (!(await confirmDialog({
        title: 'Fast track to landing?',
        message: `Close ${th.title || 'this thread'}${th.projectName ? ` on ${th.projectName}` : ''} administratively? Any open subtasks are marked complete and a "Fast-tracked" Thread Closed activity is recorded — same records as a worked close, minus the ceremony. Reversible via Reopen on the thread.`,
        confirmLabel: 'Fast track',
      }))) return
    }
    // The payoff: the helicopter flies the rest of the curve to the landing zone instead of
    // just vanishing (FlightTracker's landAnim listens for this) — fired the moment the
    // fast-track is committed, concurrent with the writes, whichever entry point started it.
    window.dispatchEvent(new CustomEvent('flight-tracker-land-anim', {
      detail: { kind: 'thread', id: th.threadId, t: opts && opts.t != null ? opts.t : undefined },
    }))
    act(th.threadId, () => fastTrackThread(th.threadId, wf.currentMemberId, null))
  }, [act])

  // FAST TRACK a milestone (2026-08-29) — for work that is genuinely DONE but never posted.
  // Matt confirmed the real case: Agan's PPC 2nd Submission is "complete, approved entire and with
  // the contractor," sitting unposted while the scheduler pushed its date 19 times.
  //
  // Completes every remaining checklist item through the REAL per-item path (so each carries proper
  // completed-by attribution — never a silent bulk flag), then hands off to the ordinary post modal
  // prefilled to today. It deliberately STOPS there: posting fires client-facing side effects
  // (journey publish, titleblock burn, drawing release) and the per-type hard gates live in that
  // modal (sheet/QC approval, Rendering Package images, permit/revision links). Fast-track must
  // never route around those — a gated type will refuse, and that refusal is correct.
  //
  // ROLLBACK ON CANCEL (Matt's explicit requirement): if the post is abandoned, the items this
  // just ticked are un-ticked. A cancelled fast-track must not leave twenty items silently
  // completed on a milestone nobody actually closed. `post-ms-modal-closed` carries `posted` so
  // the two paths are distinguishable — close() alone fires on both.
  const ftRef = useRef(null)   // { msId, completedIds } while a fast-track post is pending

  const doFastTrackMilestone = useCallback(async (m) => {
    const msId = m.milestoneId
    if (!msId) return
    // Waiting on a predecessor (2026-09-24, outline/blocked.js): it cannot begin, let alone land.
    // The queue row carries the server's own `blocked` / `waitingOn`.
    if (m.blocked) {
      const w = (m.waitingOn || []).join(', ')
      await alertDialog({ title: 'Not yet', message: `${m.name} is waiting on ${w || 'a predecessor'}.` })
      return
    }
    // PASSENGERS (2026-09-09): an open thread riding this plane blocks landing, fast track included.
    try {
      const pr = await getPassengersFor([msId])
      const aboard = (pr.byMilestone || {})[msId] || []
      if (aboard.length) {
        await alertDialog({ title: 'Passengers still aboard', message: `${m.name} is carrying ${aboard.length} open thread${aboard.length === 1 ? '' : 's'}: ${aboard.map(p => p.title).join('; ')}. Close ${aboard.length === 1 ? 'it' : 'them'} first, or move ${aboard.length === 1 ? 'it' : 'them'} to a later milestone from the Post milestone window.` })
        return
      }
    } catch (_) { /* fail open, like the kit check when Airtable is unreachable */ }
    let items = []
    try { items = await getMilestoneChecklistItems(msId) }
    catch (e) { setError('Could not read the checklist: ' + String(e && e.message || e)); return }
    // 'Milestone Not Required' items are already resolved — skipping them mirrors the ⋯ menu's own
    // behaviour, which no-ops on a skipped item rather than completing it. QA/QC-category items
    // are excluded outright (2026-09-04) — same HIDDEN_CL_CATEGORIES exclusion
    // PostMilestoneModal's own posting gate already applies. They're hidden from the outline
    // entirely (migrating to AlderQC), so fast track has no business force-completing something
    // nobody can see or verify — it just leaves them exactly as they are, same as the real
    // posting gate already does.
    const open = (items || []).filter(i => {
      const f = i.fields || {}
      const cat = (Array.isArray(f['Category (from Deliverable Checklist Item Template)'])
        ? f['Category (from Deliverable Checklist Item Template)'][0]
        : f['Category (from Deliverable Checklist Item Template)']) || 'Other'
      if (HIDDEN_CL_CATEGORIES.indexOf(cat) !== -1) return false
      return f['Complete'] !== true && f['Milestone Not Required'] !== true
    })
    if (!(await confirmDialog({
      title: 'Fast track to landing?',
      message: open.length
        ? `Mark ${open.length} remaining checklist item${open.length === 1 ? '' : 's'} complete on ${m.name}, then open the post window? If you cancel the post, those ${open.length} item${open.length === 1 ? '' : 's'} will be un-ticked again.`
        : `The checklist on ${m.name} is already complete. Open the post window?`,
      confirmLabel: 'Fast track',
    }))) return

    setBusy(msId); setError('')
    const completed = []
    try {
      for (const it of open) {
        await completeChecklistItem(it.id, true, wf.currentMemberId)
        completed.push(it.id)
      }
    } catch (e) {
      // Partial failure: undo what we managed, so the milestone is left as we found it.
      for (const id of completed) {
        try { await completeChecklistItem(id, false, wf.currentMemberId) } catch (_) { /* best effort */ }
      }
      setBusy(''); setError('Fast track stopped and was rolled back: ' + String(e && e.message || e))
      return
    }
    ftRef.current = { msId, completedIds: completed }
    setBusy('')
    window.dispatchEvent(new CustomEvent('open-post-ms-modal', { detail: { msId } }))
  }, [])

  // The rollback half. Fires whether the post succeeded or was abandoned.
  useEffect(() => {
    const onClosed = async (e) => {
      const pending = ftRef.current
      const d = (e && e.detail) || {}
      if (!pending || (d.msId && d.msId !== pending.msId)) return
      ftRef.current = null
      if (d.posted) { load(); return }           // real post — the board reload lands the flight
      if (!pending.completedIds.length) return
      setBusy(pending.msId)
      for (const id of pending.completedIds) {
        try { await completeChecklistItem(id, false, wf.currentMemberId) } catch (_) { /* best effort */ }
      }
      setBusy('')
      setError(`Post cancelled — the ${pending.completedIds.length} checklist item${pending.completedIds.length === 1 ? '' : 's'} fast track had ticked have been un-ticked.`)
    }
    window.addEventListener('post-ms-modal-closed', onClosed)
    return () => window.removeEventListener('post-ms-modal-closed', onClosed)
  }, [load])

  // Judged-position move for a thread — the SAME required-note contract as dragging a milestone
  // flight on the curve (a hill chart without the note is a prettier percentage), EXCEPT for the
  // very first move: launching a thread (isSeedThread — still sitting untouched at SEED_T) is
  // covered whether it's the "Put in the air" button or a drag straight off the deck, since both
  // call this same function. Matt, 2026-08-31: "putting them in the air doesn't require
  // explanation" — there's nothing to explain yet at that instant, only once it's actually
  // moving does a note carry any signal. Mirrors the same launch exemption on the milestone side
  // (createFlight never asks for a note either — only a milestone flight's later moves, via
  // onMoveHill below, do).
  //
  // 2026-09-04 (Matt: "don't let an uncrewed flight enter the air... that would cause a crash,
  // extend the metaphor"): launching a thread for the first time now opens the SAME CrewPicker a
  // milestone's "Put in the air" already does, instead of moving it straight away. `hill` rides
  // along on the target (`target.hill`) so onSave's thread branch knows this crewing IS the
  // launch and must also perform the actual move — a bare "Crew this thread…" backup assignment
  // on an already-flying thread (no `hill`) must not.
  const moveThreadHill = useCallback(async (th, t) => {
    if (isSeedThread(th)) {
      setCrewing({ thread: th, hill: t })
      return
    }
    const note = await promptDialog({
      title: `Move ${th.title || 'this thread'}`,
      message: hillLabel(t) + ' — what changed?',
      placeholder: 'e.g. waiting on the structural engineer, response drafted',
      multiline: true, required: true,
    })
    if (note === null) return
    await act(th.threadId, () => setThreadHill(th.threadId, t, note))
  }, [act])

  // Multi-select on the "Next up" list (2026-09-01) — replaces the two separate modal pickers
  // (ThreadTriageModal / SquadronBuilderModal, both retired). Matt: "I would rather be able to
  // select from the main list, not a list that pops up in a modal... enter into multi-select
  // mode on the next up list and start checking boxes... the actions that are available show up
  // as buttons... it also handles filtering the lists elegantly as we can filter this list by
  // project already." Threads only — every bulk action here (fast track, bundle, add-to-
  // squadron) is thread-shaped; milestone rows are untouched by select mode. Scoped to the Next
  // Up list specifically, per Matt's own words, not "In the air" — a thread already on the curve
  // has already had some attention, and the old modals' candidate lists came from the SAME open,
  // unbundled thread set Next Up already renders, filtered the same way (project scope lives in
  // FlightTracker's own picker and already narrows `allThreads`/`queue.threads` before this ever
  // sees them — no separate filtering to duplicate).
  //
  // null = not selecting. 'generic' = the plain "Select…" entry — Fast track / Bundle into a
  // NEW squadron / Add to an existing one are all on the table, and checkboxes are never
  // disabled (a scattered fast-track batch across projects is completely valid; only the
  // BUTTONS available narrow based on what's actually selected). A squadron flight object =
  // entered via that squadron's own "Add threads…" action menu item — scoped to adding into
  // that ONE unit, the only action offered, so checkboxes outside its project or past its
  // remaining capacity are disabled rather than just producing a dead button.
  const [selectMode, setSelectMode] = useState(null)
  const [selectedThreadIds, setSelectedThreadIds] = useState(() => new Set())
  // threadId -> 'working' | 'done' | error string, live per-row feedback during a bulk run.
  // Left populated after a run (not auto-cleared) so a partial failure stays visible and
  // re-running the same action skips whatever already succeeded.
  const [bulkStatus, setBulkStatus] = useState({})
  const [bulkRunning, setBulkRunning] = useState(false)
  const nextUpRef = useRef(null)
  const addSquadronMenu = useActionMenu()   // the "Add to squadron ▾" dropdown when more than one candidate exists
  // "Next up" plane/helicopter filter — plain local state, not wf.collapsed: this is an
  // ephemeral view toggle on one tab's list, not a node's persistent expand/collapse state.
  // Declared here (moved up 2026-09-01) because enterSelectMode's dependency array reads it —
  // a real production crash: a useCallback's own dep array is evaluated during THIS render, not
  // deferred like the callback body is, so referencing a const declared later in the same
  // function body threw "Cannot access 'queueFilter' before initialization" on every render.
  const [queueFilter, setQueueFilter] = useState('both')   // 'both' | 'flights' | 'threads'

  const exitSelectMode = useCallback(() => {
    setSelectMode(null)
    setSelectedThreadIds(new Set())
    setBulkStatus({})
  }, [])

  const enterSelectMode = useCallback((mode) => {
    setSelectMode(mode)
    setSelectedThreadIds(new Set())
    setBulkStatus({})
    if (queueFilter === 'flights') setQueueFilter('both')   // nothing to select if threads are hidden
    // Entering from a squadron's own "Add threads…" menu can happen from way up on the curve —
    // scroll the list the checkboxes actually live in into view rather than leaving it a mystery.
    nextUpRef.current && nextUpRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [queueFilter])

  const toggleThreadSelected = useCallback((id) => {
    setSelectedThreadIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }, [])

  // Drop one thread out of a squadron back to flying solo — its own individual tracking resumes
  // on the next board load (see removeThreadFromSquadron's own doc comment). `m` is the member
  // object OnCurveSquadron passes ({ threadId, title, type, projectId }).
  const doRemoveSquadronThread = useCallback((f, m) => {
    act(f.flightId, () => removeThreadFromSquadron(f.flightId, m.threadId))
  }, [act])

  // Pulling a thread off the curve doesn't close it — it just resets the judged position. The
  // next board load re-seeds it fresh at the ground if it's still open in Airtable (see
  // api/thread-hill.js's own seeding logic), the same non-destructive shape as a flight being
  // "pulled from the air" without being posted.
  const doPullThread = useCallback(async (th) => {
    if (!(await confirmDialog({
      title: 'Pull from the curve?',
      message: `Pull ${th.title || 'this thread'}${th.projectName ? ` on ${th.projectName}` : ''} off the curve? It stays open — this only resets where it sits, and it reappears at the ground the next time the board loads if it's still open.`,
      confirmLabel: 'Pull from the curve',
    }))) return
    act(th.threadId, () => landThread(th.threadId))
  }, [act])

  if (loading && !plan) return <div style={S.filler}>Loading the flight plan…</div>
  if (error && !plan) return <div style={{ ...S.filler, color: PALETTE.red }}>{error}</div>
  if (!plan) return <div style={S.filler}>No flight plan yet.</div>

  const { today, flights, projects, grounded, holding, stats, crewLoad } = plan
  // Squadrons (database/202_squadrons.sql) are `flights` rows too, but FlightCard below is
  // milestone-shaped (checklist, "Mark landed" → the closeout wizard, "Fast track" → the
  // checklist-completion flow) — none of which apply to a bundle of threads. Split them out here
  // and render squadrons with their own, simpler card, same split FlightTracker makes on the curve.
  const milestoneFlights = flights.filter(f => f.kind === 'milestone')
  // Like an arrivals board (Matt, 2026-10-27): ordered by where each plane is on the flight path, closest
  // to landing first, reversible. Ties fall back to the due date.
  const sortedFlights = milestoneFlights.slice().sort((a, b) =>
    ((landingFirst ? -1 : 1) * ((a.hillPosition || 0) - (b.hillPosition || 0))) || String(a.latestFinish || '9').localeCompare(String(b.latestFinish || '9')))
  const squadronsInAir = flights.filter(f => f.kind === 'squadron')
  // Crewing a bare thread (2026-09-01) mints a `kind:'thread'` flight too — same reasoning as
  // Squadrons, sharing the crew-capacity pool for free — but it is NEVER its own row anywhere.
  // It exists purely so the trigger has something to count; the real thread already renders via
  // thread_hill below. Joined back onto the matching thread object by threadId, right where
  // `allThreads` itself is built, so every consumer of `allThreads` sees `.crew`/`.crewFlightId`
  // without needing to know this second source exists.
  const threadCrewByThreadId = new Map(
    flights.filter(f => f.kind === 'thread').map(f => [f.threadId, f]))
  // Only threads someone has actually judged count as "in the air" — Matt: "I noticed you have
  // all of our threads in flight, but they shouldn't be. They should wait for us to put them in
  // flight." A thread still sitting at its seed position lives in `queue.threads` instead (see
  // ThreadDeck in FlightTracker.jsx) and shows in the "Next up" list below, not here.
  //
  // STABLE WHEN EMPTY (2026-09-24). This used to be `(... || []).map(...)` — a brand-new array on
  // every render even though threadPlan has been null since threads became passengers (09-09). Passed
  // down as the chart's `threads` prop, that new array re-ran FlightTracker's queue memos, whose
  // report effect called setQueue here with a new object, which rendered this again: a permanent
  // ~9 commits/s re-render loop producing identical output. It kept every Flight Board tab's renderer
  // at 100% CPU and 1–2 GB (outside the JS heap, so performance.memory looked fine) until Chrome
  // killed it "Out of Memory". Returning one shared EMPTY when there are no threads is what breaks
  // the cycle; the chart no longer receives the thread lists at all (see the FlightTracker props).
  // Not a hook on purpose: this sits below the component's early "loading" return, so a useMemo here
  // changes the hook count between renders (React error #310, a blank page — caught on the preview).
  // The shared EMPTY is what matters; a non-empty list (none exist today) may still be rebuilt per
  // render, and FlightTracker's report effect guards against that by comparing ids.
  const threadSrc = threadPlan && threadPlan.threads
  const allThreads = (!threadSrc || !threadSrc.length) ? EMPTY : threadSrc.map(th => {
    const cf = threadCrewByThreadId.get(th.threadId)
    return cf ? { ...th, crew: cf.crew, crewFlightId: cf.flightId, crewFlightNumber: cf.flightNumber } : th
  })
  const threadsInAir = allThreads.filter(th => !isSeedThread(th))

  // ── Next Up multi-select — derived selection shape ──────────────────────────────────────
  const selecting = !!selectMode
  const addTarget = (selectMode && selectMode !== 'generic') ? selectMode : null
  const selectedThreads = allThreads.filter(th => selectedThreadIds.has(th.threadId))
  const selCount = selectedThreads.length
  // Generic mode's buttons narrow to what the CURRENT selection actually supports — Fast track
  // has no project constraint at all (a scattered batch is a completely valid triage run), so
  // only Bundle/Add care about this.
  const selProjectIds = new Set(selectedThreads.map(th => th.projectId).filter(Boolean))
  const selSameProject = selCount > 0 && selProjectIds.size === 1
  const selProjectId = selSameProject ? [...selProjectIds][0] : null
  const canBundleNew = !addTarget && selSameProject && selCount >= 2 && selCount <= 5
  const candidateSquadrons = (!addTarget && selSameProject)
    ? squadronsInAir.filter(f => f.projectId === selProjectId && (f.squadronThreads || []).length + selCount <= 5)
    : []

  const bulkFastTrack = async () => {
    const targets = selectedThreads.filter(th => bulkStatus[th.threadId] !== 'done')
    if (!targets.length) return
    if (!(await confirmDialog({
      title: `Fast track ${targets.length} thread${targets.length === 1 ? '' : 's'}?`,
      message: 'Each one gets its open subtasks completed and a "Fast-tracked" Thread Closed activity recorded — same records as a worked close, minus the ceremony. Reversible per thread (Reopen) if one turns out to still be live.',
      confirmLabel: `Fast track ${targets.length}`,
    }))) return
    setBulkRunning(true)
    const results = {}
    for (const th of targets) {
      setBulkStatus(s => ({ ...s, [th.threadId]: 'working' }))
      try {
        await fastTrackThread(th.threadId, wf.currentMemberId, null)
        results[th.threadId] = 'done'
      } catch (e) {
        results[th.threadId] = String(e && e.message || e)
      }
      setBulkStatus(s => ({ ...s, [th.threadId]: results[th.threadId] }))
    }
    setBulkRunning(false)
    if (Object.values(results).some(v => v === 'done')) await load()
  }

  // 2026-09-04 (Matt: "don't let an uncrewed flight enter the air"): a squadron used to mint
  // uncrewed at the gate (hill 0.08, members null) with crewing left for "afterward, from its
  // own action menu" — exactly the gap Matt flagged. Now this opens the same CrewPicker every
  // other launch uses; onSave's `t.squadron` branch creates it WITH the picked crew in one call,
  // so an uncrewed squadron is never minted at all. Canceling the picker creates nothing.
  // 2026-09-04 (Matt: "allow me to combine flying threads into squadrons if they are of the same
  // project and the crew would not be overburdened"). A selection can now include already-flying
  // threads (ThreadCard's own selecting support, above) alongside or instead of queued ones.
  // Crew handling branches on what's already there rather than always opening the picker:
  //   - None of the selected threads carry a crew (the all-queued case, unchanged from before) →
  //     open the CrewPicker, same as any other launch — a squadron never enters the air uncrewed.
  //   - Some already-flying threads carry crew → union those crews (dedup by member). Reusing
  //     whoever is ALREADY on these exact threads can never overburden anyone — it's the same
  //     assignments consolidating under one flight, not a new one — so no capacity re-check is
  //     needed beyond fitting the squadron's own 2-seat cap. Skips the picker entirely.
  //   - The union is too big to crew a single flight → refuse outright rather than guess who to
  //     drop; the message says why and leaves the selection as-is so it can be narrowed.
  // Either way, createSquadron mints the flight WITH its final crew in one call (never uncrewed),
  // and the backend now releases any individual thread-crew flight being folded in — see
  // releaseIndividualThreadCrew in api/flights.js.
  const bulkBundleSquadron = async () => {
    const ids = selectedThreads.map(th => th.threadId)
    if (ids.length < 2) return
    const cap = (plan && plan.maxCrew) || 2
    const crewById = new Map()
    for (const th of selectedThreads) for (const c of (th.crew || [])) crewById.set(c.memberAirtableId, c)
    const unionCrew = [...crewById.values()]
    if (unionCrew.length > cap) {
      setError(`Combining these needs ${unionCrew.length} people already flying them, but a squadron carries at most ${cap}. Drop one from the selection, or re-crew one down first.`)
      return
    }
    const hill = Math.max(0.08, ...selectedThreads.map(th => th.hillPosition || 0.08))
    if (unionCrew.length > 0) {
      setError('')
      try {
        await createSquadron(ids, hill, unionCrew.map(c => ({ memberAirtableId: c.memberAirtableId, memberName: c.name, isCaptain: !!c.isCaptain })))
        await load()
        exitSelectMode()
      } catch (e) {
        setError(String(e && e.message || e))
      }
    } else {
      setCrewing({ squadron: { threads: selectedThreads, threadIds: ids, hill } })
    }
  }

  const bulkAddToSquadron = async (targetFlightId) => {
    const targets = selectedThreads.filter(th => bulkStatus[th.threadId] !== 'done')
    if (!targets.length || !targetFlightId) return
    setBulkRunning(true)
    const results = {}
    for (const th of targets) {
      setBulkStatus(s => ({ ...s, [th.threadId]: 'working' }))
      try {
        await addThreadToSquadron(targetFlightId, th.threadId)
        results[th.threadId] = 'done'
      } catch (e) {
        results[th.threadId] = String(e && e.message || e)
      }
      setBulkStatus(s => ({ ...s, [th.threadId]: results[th.threadId] }))
    }
    setBulkRunning(false)
    const allDone = targets.every(th => results[th.threadId] === 'done')
    if (Object.values(results).some(v => v === 'done')) await load()
    if (allDone) exitSelectMode()
  }

  // Emergency-landing candidates: flights whose PHASE is running low, not the flight's own
  // budget (there isn't one reliable enough to use — see api/flights.js's own "Fuel" doc
  // comment). Counted here rather than as a server stat since it's a plain filter over data
  // the board already has. Reads `ebrStatus` (2026-09-03) — the EBR-based gauge, not the
  // budget-remaining `status` that still ranks Next Up.
  const lowFuel = flights.filter(f => f.fuel && f.fuel.ebrStatus !== 'On Track' && f.fuel.ebrStatus !== 'No Budget Set')
  // Full kit (Step 2): planes in the air with Full-Kit items still open — the count clears itself
  // as the items get ticked, so this is a to-do for the crew, not a permanent mark.
  const openKit = flights.filter(f => f.kit && f.kit.preworkOpen > 0)

  // Double-booking is refused by the database, so there is nothing to warn about — the picker
  // greys out anyone already flying instead.
  const flying = Object.keys(crewLoad || {}).length
  // Resource counts for the chart's own corner readout — Matt: "a list of resource counts
  // showing how many are in the air and how many are available to join a crew." Landing a
  // flight already releases its crew server-side (releaseCrewOf in api/flights.js), and this
  // board reloads after every mutation, so `flying` is never stale by more than one round trip.
  const crewAvailable = Math.max(0, team.length - flying)
  // The hover popover's own table — Matt sent a design (2026-09-02, "Pilot view table mockup")
  // upgrading the old two-column flying/ground list into a folded-ticket-style table: every crew
  // member gets exactly 2 SLOTS (matching the real 2-assignment cap), each showing a status
  // plane icon + flight tag folded, or status word + arrival + delay once unfolded. `flights`
  // carries flightId/projectName/name for flightTag(); crewLoad is memberId -> [{flightId}, ...]
  // (up to 2, since 2026-09-01), so this is a join over data already on the board, not a new
  // fetch.
  const flightById = new Map(flights.map(f => [f.flightId, f]))
  const threadById = new Map(allThreads.map(th => [th.threadId, th]))
  // A thread-crew flight's OWN hill_position/dates are just placeholders (see crewThread's own
  // comment in api/flights.js — the row exists purely for capacity accounting) — the real
  // position and due date live on the joined thread object, so kind='thread' resolves through
  // it instead of trusting the flight row's own fields. Squadrons have no single due date (they
  // bundle threads with their own separate ones); `due` stays null there rather than fabricating
  // one, and the table shows a dash.
  const describeAssignment = (a) => {
    const f = flightById.get(a.flightId)
    if (!f) return null
    const th = f.kind === 'thread' ? threadById.get(f.threadId) : null
    const pos = th ? th.hillPosition : f.hillPosition
    const due = f.kind === 'squadron' ? null : ((th ? th.targetDeadline : f.latestFinish) || null)
    const late = !!(due && due < today)
    const lateBy = late ? Math.round((new Date(today + 'T00:00:00') - new Date(due + 'T00:00:00')) / 86400000) : 0
    return {
      tag: th ? threadTag(th) : flightTag(f), status: hillLabel(pos), due, late,
      delay: due ? (late ? `${lateBy}d` : 'On Time') : null, flightId: a.flightId, priority: a.priority,
      // The same objects the curve's own hover cards already read (HoverCard/SquadronHoverCard/
      // ThreadHoverCard in FlightTracker.jsx) — carried through so the Pilots tab can show the
      // identical popup on hover instead of a second, thinner one (Matt, 2026-09-10: "can I get
      // the same popup info that shows on the curve, please?").
      kind: f.kind || 'milestone', flight: f, thread: th,
    }
  }
  // The Pilots table only ever shows `eligibleCrew(team, roster)` — same filter the crew picker
  // already applies — so someone switched off in Admin → Crew (a placeholder account, a seat
  // that never flies, a departed hire) doesn't sit in this list looking flyable (Matt, 2026-09-10:
  // "There are team members not selected as crew but they still show in the pilots list. They
  // shouldn't."). Each slot's own assignment now carries its flightId + priority so the rank
  // pill and the reassign picker below can act on it; assignments are sorted 1st-seat-first so
  // slot 0 is always someone's first-priority plane when they have one.
  const crewTable = eligibleCrew(team, roster).map(t => {
    const assignments = ((crewLoad || {})[t.id] || []).slice().sort((a, b) => (a.priority || 99) - (b.priority || 99))
    const slots = [0, 1].map(i => {
      const a = assignments[i]
      const d = a && describeAssignment(a)
      return d ? { flying: true, ...d } : { flying: false }
    })
    return { memberId: t.id, name: t.name, slots }
  }).sort((a, b) => a.name.localeCompare(b.name))

  // Flights that can still take a seat — the Pilots tab's "+ Add to flight" picker (2026-09-10,
  // Matt: "add them to a flight if they have an open slot ... for eligible flights (in the air
  // and not already crewed by two)"). Individual thread-crew flights are excluded — they mint a
  // bare capacity-accounting row with no real name, never shown elsewhere on this board.
  const assignableFlights = flights
    .filter(f => f.kind !== 'thread' && (f.crew || []).length < (plan.maxCrew || 2))
    .map(f => ({ flightId: f.flightId, label: `${flightTag(f)} — ${f.milestoneName || (f.kind === 'squadron' ? 'Squadron' : f.projectName || '')}` }))
    .sort((a, b) => a.label.localeCompare(b.label))

  // Shared by both crew edits below (and by CrewPicker's re-crew save further down) — patches ONE
  // already-open flight's crew line + crewLoad from a member list we already know is correct,
  // since we built it ourselves before calling setFlightCrew.
  function crewPatch(flightId, oldMembers, newMembers) {
    return (_, p) => {
      const f = p.flights.find(x => x.flightId === flightId)
      return {
        ...p,
        flights: p.flights.map(x => x.flightId === flightId
          ? { ...x, crew: newMembers.map(m => ({ memberAirtableId: m.memberAirtableId, name: m.memberName, isCaptain: !!m.isCaptain, priority: m.priority || null })) }
          : x),
        crewLoad: patchCrewLoadForFlight(p.crewLoad, flightId, (oldMembers || []).map(m => m.memberAirtableId), newMembers,
          { flightNumber: f ? f.flightNumber : '', projectName: f ? f.projectName : '' }),
      }
    }
  }

  // Swap which of a person's two open seats is their 1st vs 2nd priority. setFlightCrew re-crews
  // the WHOLE flight, so this resends its current crew unchanged except this one member's rank —
  // the server's claimPriority then does the actual swap against their OTHER open seat.
  function changeCrewPriority(memberId, flightId, newPriority) {
    const f = flightById.get(flightId)
    if (!f) return
    const members = (f.crew || []).map(c => ({
      memberAirtableId: c.memberAirtableId, memberName: c.name, isCaptain: c.isCaptain,
      priority: c.memberAirtableId === memberId ? newPriority : c.priority,
    }))
    act(memberId, () => setFlightCrew(flightId, members), crewPatch(flightId, f.crew, members))
  }

  // Seat an eligible, currently-grounded (or one-seat) pilot onto a flight with room, straight
  // from the pilots list. No priority is sent for the new seat — the server gives them whichever
  // rank they don't already hold (their first, if this is their only assignment); the optimistic
  // patch shows the seat filled right away with the rank still blank until the background
  // reload fills it in — self-corrects within moments, never a wrong or stuck value.
  function assignCrewToFlight(memberId, memberName, flightId) {
    const f = flightById.get(flightId)
    if (!f) return
    const members = [
      ...(f.crew || []).map(c => ({ memberAirtableId: c.memberAirtableId, memberName: c.name, isCaptain: c.isCaptain, priority: c.priority })),
      { memberAirtableId: memberId, memberName, isCaptain: false },
    ]
    act(memberId, () => setFlightCrew(flightId, members), crewPatch(flightId, f.crew, members))
  }

  return (
    <div style={S.wrap}>
      {error && <div style={S.error}>{error}</div>}

      {stats.postedSource === 'mirror-stale' && (
        <div style={S.warn}>
          Airtable could not be reached, so "is it posted" is running on the scheduling mirror.
          That mirror lags badly and will show finished work as overdue. Treat these numbers as
          provisional and refresh.
        </div>
      )}

      {/* The stats card is gone, not just its numbers hidden (Round 14 only hid the <Stat>
          row, which left an empty bordered box sitting here doing nothing but holding a
          Refresh button — Matt: "the card under the top bar is basically empty. Can you
          remove that card line?" That Refresh button wasn't actually redundant with the
          workspace-level one above it (TeamOverviewWorkspace's own `refresh` reloads the
          Anatomy tab's data, never FlightPlanTab's) — so it moved into FlightTracker's own
          header instead, next to the "Whole firm" scope picker, rather than disappearing.
          SHOW_STATS_BAR still restores the full card (numbers AND its own refresh button)
          if it's ever turned back on. */}
      {SHOW_STATS_BAR && (
        <div style={S.stats}>
          <Stat n={stats.activeOpen} label="milestones open" />
          <Stat n={stats.inTheAir} label="in the air" color={stats.inTheAir ? PALETTE.blue : undefined} />
          <Stat n={stats.overdue} label="overdue" color={stats.overdue ? PALETTE.red : undefined} />
          <Stat n={stats.future} label="scheduled ahead" color={stats.future ? PALETTE.green : undefined} />
          <Stat n={stats.holding} label="in holding" />
          <Stat n={flying} label="people flying" />
          {stats.blocked > 0 && (
            <Stat n={stats.blocked} label="blocked on a predecessor" color={stats.blockedOverdue ? PALETTE.red : undefined} />
          )}
          {lowFuel.length > 0 && (
            <Stat n={lowFuel.length} label="low on fuel" color={fuelTone(lowFuel.some(f => f.fuel.ebrStatus !== 'Watch') ? 'At Risk' : 'Watch')} />
          )}
          {openKit.length > 0 && (
            <Stat n={openKit.length} label="flying without a full kit" color={PALETTE.amber} />
          )}
          {stats.undated > 0 && <Stat n={stats.undated} label="undated" color={PALETTE.amber} />}
          {stats.unresolvedProject > 0 && <Stat n={stats.unresolvedProject} label="no project name" color={PALETTE.amber} />}
          {threadPlan && (
            <Stat n={threadsInAir.length} label="threads in flight" color={PALETTE.teal} />
          )}
          {threadPlan && queue.threads.length > 0 && (
            <Stat n={queue.threads.length} label="threads waiting" color={PALETTE.amber} />
          )}
          {threadPlan && threadPlan.stats.stalled > 0 && (
            <Stat n={threadPlan.stats.stalled} label="threads not moved in a week" color={PALETTE.amber} />
          )}
          <div style={{ flex: 1 }} />
          <button style={S.btn} onClick={load} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      )}


      {/* The legs of the flight live on the Tower tab (Matt, 2026-09-07: the strip here duplicated
          it). LegsStrip is still defined below and exported for TowerTab; the launch check
          (askThenCrew) is unchanged. */}

      {/* ── The hill ───────────────────────────────────────────────────────── */}
      <FlightTracker
        // Any of this tab's own modals being open force-clears every hover card on the board —
        // hover release can't be trusted to pointer events once an overlay (especially the peek
        // modal's iframe) is on top, so the board stops relying on them entirely while one is up.
        overlayOpen={!!(peek || crewing || picking || gateCheck)}
        flights={flights} projects={projects} today={today} busy={busy} checklists={checklists}
        landed={(plan && plan.landed) || []}
        onHold={openHold}
        onOpenFlight={(f) => openMs(f)}
        onOpenLanded={(f) => openMs(f)}
        onOpenLandedThread={(th) => openThread(th)}
        onDropNew={(m, t) => askThenCrew(m, t, setCrewing, setError, setGateCheck)}
        crewFlying={flying} crewAvailable={crewAvailable}
        crewTable={crewTable}
        assignableFlights={assignableFlights}
        onChangeCrewPriority={changeCrewPriority}
        onAssignCrewToFlight={assignCrewToFlight}
        onQueueChange={setQueue}
        onRefresh={load} refreshing={loading}
        onCrew={(f) => setCrewing({ flight: f })}
        onLand={doLand}
        onMoveHill={async (f, t) => {
          // The note is required, not optional. A hill chart without it is a prettier
          // percentage — the position says where the work is, the note says what was learned,
          // and only the pair makes drift visible over time.
          const note = await promptDialog({
            title: `Move ${flightTag(f)}`,
            message: hillLabel(t) + ' — what changed?',
            placeholder: 'e.g. structural approach settled, just drafting now',
            multiline: true, required: true,
          })
          if (note === null) return
          // Awaited: FlightTracker freezes the plane at the drop point until this promise
          // settles. That used to mean waiting for the full board reload before the plane had a
          // correct place to be — now the patch sets the real hillPosition the instant the write
          // succeeds, so the freeze can release immediately with nowhere left to snap back from.
          await act(f.flightId, () => setFlightHill(f.flightId, t, note, wf.currentMemberId), (_, p) => ({
            ...p,
            flights: p.flights.map(x => x.flightId === f.flightId
              ? { ...x, hillPosition: t, hillMovedAt: new Date().toISOString(), stillDays: 0 }
              : x),
          }))
        }}
        onMoveSquadronHill={async (f, t) => {
          // A squadron is a flight — setFlightHill already works for it, kind-agnostic, no
          // backend change needed. Same required-note contract as everything else on the curve
          // (2026-09-01 — squadrons could not be dragged at all before this).
          const note = await promptDialog({
            title: `Move ${f.flightNumber}`,
            message: hillLabel(t) + ' — what changed?',
            placeholder: 'e.g. waiting on two of the three responses, one drafted',
            multiline: true, required: true,
          })
          if (note === null) return
          await act(f.flightId, () => setFlightHill(f.flightId, t, note, wf.currentMemberId), (_, p) => ({
            ...p,
            flights: p.flights.map(x => x.flightId === f.flightId
              ? { ...x, hillPosition: t, hillMovedAt: new Date().toISOString(), stillDays: 0 }
              : x),
          }))
        }}
        // Threads are PASSENGERS now (docs/PASSENGERS.md): the chart draws none, so it is handed no
        // thread lists — `threads`/`landedThreads` fall back to FlightTracker's own stable EMPTY.
        showThreads={false}
        onOpenThread={(th) => openThread(th)}
        onPull={doPullThread}
        onFastTrackThread={doFastTrackThread}
        onRemoveSquadronThread={doRemoveSquadronThread}
        onAddSquadronThread={enterSelectMode}
        onCrewThread={(th) => setCrewing({ thread: th })}
        onMoveThreadHill={moveThreadHill}
        hoverRowId={rowHoverId} />

      {/* ── Flights, with their checklists — and Threads, alongside them ─────── */}
      <SectionHead title="In the air"
        action={<button type="button" style={S.sortBtn} onClick={() => { const v = !landingFirst; setLandingFirst(v); try { localStorage.setItem('aj-fp-landing-first', v ? '1' : '0') } catch { /* private window */ } }}
          title="Order the list by where each plane is on the flight path">
          {landingFirst ? 'Closest to landing first ↓' : 'Just took off first ↑'}</button>}
        meta={`${milestoneFlights.length} flight${milestoneFlights.length === 1 ? '' : 's'}${threadsInAir.length ? `, ${threadsInAir.length} thread${threadsInAir.length === 1 ? '' : 's'}` : ''}${squadronsInAir.length ? `, ${squadronsInAir.length} squadron${squadronsInAir.length === 1 ? '' : 's'}` : ''}`} />
      {milestoneFlights.length === 0 && threadsInAir.length === 0 && squadronsInAir.length === 0
        ? <div style={{ ...S.card, padding: '18px 16px' }}><div style={S.empty}>Nothing in the air yet.</div></div>
        : <div style={S.tableWrap}>
            {sortedFlights.map(f => (
              <FlightCard key={f.flightId} f={f} today={today} busy={busy === f.flightId} cl={checklists[f.milestoneId]}
                onItem={openMs} onLand={doLand} onCrew={() => setCrewing({ flight: f })}
                onFastTrack={doFastTrackMilestone}
                hovered={rowHoverId === f.flightId}
                onHoverIn={() => setRowHoverId(f.flightId)}
                onHoverOut={() => setRowHoverId(id => (id === f.flightId ? null : id))} />
            ))}
            {threadsInAir.map(th => {
              // Same selection wiring as QueuedThreadRow below — a flying thread checks into the
              // identical `selectedThreadIds` set, so it can be combined into a squadron
              // alongside (or instead of) queued ones. `selecting` is the same board-wide toggle
              // both lists share; only the disabled-reason text differs per list's own targeted
              // (addTarget) mode.
              const wrongProject = addTarget && th.projectId !== addTarget.projectId
              const capReached = addTarget && (addTarget.squadronThreads || []).length + selCount >= 5
              const selected = selectedThreadIds.has(th.threadId)
              return (
                <ThreadCard key={th.threadId} th={th} today={today} busy={busy === th.threadId}
                  onItem={openThread} onPull={doPullThread} onFastTrack={doFastTrackThread}
                  onCrew={() => setCrewing({ thread: th })}
                  hovered={rowHoverId === th.threadId}
                  onHoverIn={() => setRowHoverId(th.threadId)}
                  onHoverOut={() => setRowHoverId(id => (id === th.threadId ? null : id))}
                  selecting={selecting} selected={selected}
                  selectDisabled={!selected && (wrongProject || capReached)}
                  selectDisabledReason={wrongProject ? `Not on ${addTarget.projectName || 'this project'}` : (capReached ? `${addTarget.flightNumber} is already at the 5-thread cap` : null)}
                  onToggleSelect={() => { if (!selected && (wrongProject || capReached)) return; toggleThreadSelected(th.threadId) }} />
              )
            })}
            {squadronsInAir.map(f => (
              <SquadronCard key={f.flightId} f={f} busy={busy === f.flightId}
                hovered={rowHoverId === f.flightId}
                onHoverIn={() => setRowHoverId(f.flightId)}
                onHoverOut={() => setRowHoverId(id => (id === f.flightId ? null : id))}
                onCrew={() => setCrewing({ flight: f })} onLand={doLand}
                onOpenThread={openThread} onRemoveThread={doRemoveSquadronThread}
                onAddThread={enterSelectMode} />
            ))}
          </div>}

      {/* ── Next up — the same queue the gate stacks show, as an actual readable list ────────
          Matt: "the list at the top to show those in flight and then under that list another
          list showing the next ones coming up for flight." Milestones here aren't flights yet
          (no crew, not on the curve); threads here have never been given a judged position
          (still at their seed t — see isSeedThread). Nothing in this section has its own
          checklist body or ⋯ menu — clicking a row opens it, same as everywhere else; "Put in
          the air" reuses the exact crew-picker flow dragging onto the curve already opens.
          `queueFilter` (2026-08-31, Matt: "a toggle... to show either planes, helicopters, or
          both") narrows which of the two lists render — it filters the RENDERED rows only, never
          `queue` itself, so nothing else reading that object (the hidden list, the gate stacks) is
          affected by what's chosen here. */}
      {(() => {
        const showFlights = queueFilter !== 'threads'
        const showThreads = queueFilter !== 'flights'
        const visFlights = showFlights ? queue.flights : []
        const visThreads = showThreads ? queue.threads : []
        return (<div ref={nextUpRef}>
          <SectionHead title="Next up"
            meta={`${visFlights.length} milestone${visFlights.length === 1 ? '' : 's'}${visThreads.length ? `, ${visThreads.length} thread${visThreads.length === 1 ? '' : 's'}` : ''}`}
            action={<div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <QueueFilterToggle value={queueFilter} onChange={setQueueFilter} />
              {!selecting && allThreads.length > 0 && (
                <button style={S.btn} onClick={() => enterSelectMode('generic')}>Select…</button>
              )}
              {selecting && (<>
                <span style={S.selCount}>
                  {addTarget
                    ? `Adding to ${addTarget.flightNumber} — ${selCount} selected`
                    : (selCount ? `${selCount} selected` : 'Check threads below')}
                </span>
                {/* Targeted add mode (opened from a squadron's own "Add threads…" menu) offers
                    exactly the one action it was opened for — Fast track / Bundle a new unit
                    would be a surprising thing to see mixed into "I want to grow SQD1." Generic
                    mode (the plain "Select…" entry) offers whatever the current selection
                    actually supports, computed above. */}
                {!addTarget && selCount > 0 && (
                  <button style={S.btn} disabled={bulkRunning} onClick={bulkFastTrack}
                    title="Complete open subtasks and record a Fast-tracked closure on each">
                    {bulkRunning ? 'Working…' : `Fast track (${selCount})`}
                  </button>
                )}
                {!addTarget && canBundleNew && (
                  <button style={S.btn} onClick={bulkBundleSquadron}
                    title="Opens the crew picker — the squadron isn't created until you confirm a crew">
                    {`Bundle into squadron (${selCount})`}
                  </button>
                )}
                {!addTarget && candidateSquadrons.length === 1 && (
                  <button style={S.btn} disabled={bulkRunning}
                    onClick={() => bulkAddToSquadron(candidateSquadrons[0].flightId)}>
                    {bulkRunning ? 'Working…' : `Add to ${candidateSquadrons[0].flightNumber} (${selCount})`}
                  </button>
                )}
                {!addTarget && candidateSquadrons.length > 1 && (
                  <>
                    <button style={S.btn} disabled={bulkRunning}
                      onClick={(e) => addSquadronMenu.toggle(e.currentTarget)}>
                      Add to squadron… ({selCount})
                    </button>
                    <ActionMenu menu={addSquadronMenu}>
                      {candidateSquadrons.map(f => (
                        <ActionMenuItem key={f.flightId}
                          onClick={() => { addSquadronMenu.close(); bulkAddToSquadron(f.flightId) }}>
                          {f.flightNumber} ({(f.squadronThreads || []).length} thread{(f.squadronThreads || []).length === 1 ? '' : 's'})
                        </ActionMenuItem>
                      ))}
                    </ActionMenu>
                  </>
                )}
                {addTarget && selCount > 0 && (
                  <button style={S.btn} disabled={bulkRunning} onClick={() => bulkAddToSquadron(addTarget.flightId)}>
                    {bulkRunning ? 'Working…' : `Add (${selCount})`}
                  </button>
                )}
                <button style={S.btn} onClick={exitSelectMode} disabled={bulkRunning}>Done</button>
              </>)}
            </div>} />
          {visFlights.length === 0 && visThreads.length === 0
            ? <div style={{ ...S.card, padding: '18px 16px' }}>
                <div style={S.empty}>{queue.flights.length === 0 && queue.threads.length === 0 ? 'Nothing queued.' : 'Nothing matches this filter.'}</div>
              </div>
            : <div style={S.tableWrap}>
                {visFlights.map(m => (
                  <QueuedMilestoneRow key={m.milestoneId} m={m} today={today}
                    onOpen={openMs} onPutInAir={() => askThenCrew(m, null, setCrewing, setError, setGateCheck)}
                    onFastTrack={doFastTrackMilestone} busy={busy === m.milestoneId} />
                ))}
                {visThreads.map(th => {
                  const wrongProject = addTarget && th.projectId !== addTarget.projectId
                  const capReached = addTarget && (addTarget.squadronThreads || []).length + selCount >= 5
                  const selected = selectedThreadIds.has(th.threadId)
                  return (
                    <QueuedThreadRow key={th.threadId} th={th} today={today} onOpen={openThread}
                      onPutInAir={(t) => moveThreadHill(t, TAKEOFF_T)}
                      onFastTrack={doFastTrackThread} busy={busy === th.threadId}
                      selecting={selecting} selected={selected}
                      selectDisabled={!selected && (wrongProject || capReached)}
                      selectDisabledReason={wrongProject ? `Not on ${addTarget.projectName || 'this project'}` : (capReached ? `${addTarget.flightNumber} is already at the 5-thread cap` : null)}
                      onToggleSelect={() => { if (!selected && (wrongProject || capReached)) return; toggleThreadSelected(th.threadId) }}
                      rowStatus={bulkStatus[th.threadId]} />
                  )
                })}
              </div>}
        </div>)
      })()}
      <HiddenQueue hidden={queue.hidden} today={today}
        onOpen={openMs} onPutInAir={(m) => askThenCrew(m, null, setCrewing, setError, setGateCheck)} />


      {/* ── Grounded ───────────────────────────────────────────────────────── */}
      {grounded.length > 0 && (
        <Collapsible
          title="Grounded" defaultOpen
          meta={`${grounded.length} project${grounded.length === 1 ? '' : 's'} on hold · ${stats.grounded} milestones`}>
          {grounded.map((p, i) => (
            <ProjectRow key={p.projectId || i} p={p} i={i} today={today} grounded onHold={openHold} />
          ))}
          <div style={S.moreRow}>Held projects keep their backlog visible but cannot fly.</div>
        </Collapsible>
      )}

      {/* ── Holding ────────────────────────────────────────────────────────── */}
      {/* Full width and CLOSED by default: 153 rows of work waiting on somebody outside the
          firm is context, not the thing you act on each morning. It should be one line until
          asked for. */}
      {/* stats.holding is the true firm-wide total; `holding` itself is capped server-side
          (HOLDING_LIMIT) and undercounted here — it read "60" while the stats bar correctly
          said 153. */}
      <Collapsible title="Holding" meta={`${stats.holding} waiting on someone else, not worked${stats.feeders ? ` · ${stats.feeders} feeders` : ''}`}>
        {holding.length === 0 && <div style={S.empty}>Nothing in holding.</div>}
        {holding.map(m => (
          <div key={m.milestoneId} style={S.row}>
            <span style={{ ...S.dot, background: m.behavior === 'Fixed' ? PALETTE.teal : '#8a9ba3' }} />
            <div style={S.rowMain}>
              <div style={S.rowT} onClick={() => openMs(m)} title={m.name}>{m.projectName || 'Project not named'}</div>
              <div style={S.rowS}>
                {m.name}
                {/* Feeder (docs/OPERATING_MODEL.md step 2): who owes it and who chases it. */}
                {m.feeder && ` · owed by ${FEEDER_OWED_BY[m.feeder.owedBy] || 'someone else'}${m.feeder.coordinator ? ` · ${m.feeder.coordinator} chases` : m.feeder.roleLabel ? ` · ${m.feeder.roleLabel} not named yet` : ''}`}
              </div>
            </div>
            <span style={S.mono}>{fmtDay(m.latestFinish)}</span>
          </div>
        ))}
        {stats.holdingTruncated > 0 && <div style={S.moreRow}>{stats.holdingTruncated} more waiting</div>}
      </Collapsible>

      {/* ── Not on this board ──────────────────────────────────────────────── */}
      {/* Open milestones whose type is labeled neither work nor waiting (blank, "Standard",
          "Scheduled", "Construction Administration"). They used to appear nowhere at all (rule 10).
          The feeder setting on milestone types (docs/OPERATING_MODEL.md step 2) will sort them. */}
      {(plan.unflown || []).length > 0 && (
        <Collapsible title="Not on this board" meta={`${plan.unflown.length} open milestone${plan.unflown.length === 1 ? '' : 's'} whose type this board doesn’t fly yet`}>
          {plan.unflown.map(m => (
            <div key={m.milestoneId} style={S.row}>
              <div style={S.rowMain}>
                <div style={S.rowT} onClick={() => openMs(m)} title={m.name}>{m.projectName || 'Project not named'} · {m.name}</div>
                <div style={S.rowS}>{m.typeName || 'Type not set'} is labeled {m.behavior ? '“' + m.behavior + '”' : 'with no behavior'}, so it isn’t flown here</div>
              </div>
              <span style={S.mono}>{fmtDay(m.latestFinish)}</span>
            </div>
          ))}
        </Collapsible>
      )}

      {picking && (
        <MilestonePicker
          project={picking} today={today} onClose={() => setPicking(null)} onItem={openMs}
          onHold={(p) => { setPicking(null); openHold(p) }}
          onPick={(m) => { setPicking(null); askThenCrew(m, null, setCrewing, setError, setGateCheck) }} />
      )}

      {crewing && (
        <CrewPicker
          target={crewing} team={eligibleCrew(team, roster)} roleSkills={roleSkills} crewLoad={crewLoad} maxCrew={plan.maxCrew}
          flightById={flightById} threadById={threadById}
          onClose={() => setCrewing(null)}
          onSave={async (members) => {
            const t = crewing
            setCrewing(null)
            // t.hill is where it was dropped on the curve. A plane does not leave the ground
            // without a crew, which is why the drop opens this picker rather than creating
            // the flight outright. t.thread is the third target (2026-09-01, crewing a bare
            // thread) — its own two-state split mirrors milestone/flight exactly: no crew
            // flight yet (crewFlightId null) mints one via crewThread, otherwise it's a re-crew
            // of the existing one via the ordinary setFlightCrew path.
            //
            // 2026-09-04: t.thread's "no crew flight yet" branch now ALSO performs the actual
            // launch (setThreadHill) when t.hill is set — moveThreadHill's isSeedThread gate
            // opens this picker instead of moving the thread directly, so the move has to happen
            // here, after crewThread succeeds, not before. A bare "Crew this thread…" backup
            // assignment on an already-flying thread carries no t.hill and must not move it.
            // t.squadron is the fourth target (bulkBundleSquadron) — mints the squadron WITH its
            // crew in one call instead of the old uncrewed-then-crew-separately flow.
            if (t.milestone) {
              // "Adding a plane" — the interaction Matt named first (2026-09-10: "a lot of delay
              // ... when adding planes"). launchWithKitCheck resolves to { flight } (the real
              // inserted row — id, flight number, hill position, everything) on success, so the
              // patch builds the exact same shape `flightPlan()` returns rather than a guess; a
              // refusal/cancel (`{refused}` already shown, or `{cancelled:true}`) patches nothing.
              await act(t.milestone.milestoneId, () => launchWithKitCheck(t.milestone, members, t.hill), (result, p) => {
                if (!result || result.cancelled || !result.flight) return p
                const fl = result.flight
                const newFlight = {
                  flightId: fl.id, flightNumber: fl.flight_number, kind: 'milestone', status: fl.status || 'boarding',
                  projectId: fl.project_airtable_id || null, projectName: fl.project_name || '',
                  milestoneId: fl.milestone_airtable_id, milestoneName: fl.milestone_name || '',
                  milestoneType: fl.milestone_type || null, phaseName: null,
                  latestFinish: t.milestone.latestFinish || null, earliestStart: t.milestone.earliestStart || null,
                  threadId: null, hillPosition: fl.hill_position, hillMovedAt: fl.hill_moved_at,
                  departedAt: null, createdAt: fl.created_at,
                  crew: members.map(m => ({ memberAirtableId: m.memberAirtableId, name: m.memberName, isCaptain: !!m.isCaptain, priority: m.priority || null })),
                  threads: [],
                }
                return {
                  ...p,
                  flights: [...p.flights, newFlight],
                  crewLoad: patchCrewLoadForFlight(p.crewLoad, fl.id, [], members, { flightNumber: fl.flight_number, projectName: fl.project_name || '' }),
                }
              })
            }
            else if (t.thread) {
              if (t.thread.crewFlightId) {
                await act(t.thread.threadId, () => setFlightCrew(t.thread.crewFlightId, members))
              } else {
                await act(t.thread.threadId, async () => {
                  await crewThread(t.thread.threadId, members, t.hill)
                  if (t.hill != null) await setThreadHill(t.thread.threadId, t.hill, 'Put in the air')
                })
              }
            }
            else if (t.squadron) {
              await act('bundle-squadron', () => createSquadron(t.squadron.threadIds, t.squadron.hill ?? 0.08, members))
              exitSelectMode()
            }
            else await act(t.flight.flightId, () => setFlightCrew(t.flight.flightId, members), crewPatch(t.flight.flightId, t.flight.crew, members))
          }} />
      )}

      <EntityPeekModal target={peek} onClose={() => setPeek(null)} />
      {gateCheck && (
        <GateCheckModal milestoneId={gateCheck.m.milestoneId} name={gateCheck.m.name} projectName={gateCheck.m.projectName}
          onClose={() => { setGateCheck(null); load() }}
          onCleared={() => { const g = gateCheck; setGateCheck(null); setCrewing(g.hill == null ? { milestone: g.m } : { milestone: g.m, hill: g.hill }) }} />
      )}

      {/* ── Compact viewports (iPhone, iPad split view) — 2026-09-01, Matt: "review the way the
          flight plan will look and work on iphone... keeping the desktop appearance and
          functionality exactly the same." Same pattern as FlightTracker's own media block: every
          rule fires ONLY under 760px, so desktop rendering is byte-identical, and !important is
          what lets a class rule beat the rows' inline styles. The row treatment: fcHead wraps,
          the title block takes a full line of its own (order pushes it below the icon/number/
          date/buttons line), and the checklist meter — a 92px column that means little at a
          glance on a phone — hides; it's still one tap away inside the milestone itself. */}
      <style>{`
        @media (max-width: 760px) {
          .fp-fchead { flex-wrap: wrap !important; row-gap: 6px !important; }
          .fp-fcmain { flex-basis: 100% !important; order: 9; }
          .fp-fccl { display: none !important; }
          .fp-secth { flex-wrap: wrap !important; row-gap: 6px !important; }
        }
      `}</style>
    </div>
  )
}

// EntityPeekModal moved to EntityPeek.jsx (2026-09-23) — a thread now renders the canonical
// ThreadNode directly instead of a second copy of the app in an iframe. Re-exported for TowerTab/TriageGate.
export { EntityPeekModal }

/* ─────────────────────────────────────────────────────────────────────────────
   Lanes
   ───────────────────────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────────────────────
   Queue
   ───────────────────────────────────────────────────────────────────────────── */

/**
 * A project, with its next approaching milestone. This is the primary queue — scanning ~30
 * projects for the one to act on beats scanning 266 milestones, which is an unreadable wall.
 * Clicking opens that project's own milestones, where one gets picked for a runway.
 */
function ProjectRow({ p, i, today, grounded, onOpen, onHold }) {
  const next = p.next
  const late = next && next.latestFinish && next.latestFinish < today
  return (
    <div
      style={{ ...S.row, cursor: grounded ? 'default' : 'pointer', opacity: grounded ? 0.72 : 1 }}
      onClick={grounded ? undefined : onOpen}
    >
      <span style={{ ...S.mono, width: 18, color: 'var(--aj-text-muted)' }}>{i + 1}</span>
      <div style={S.rowMain}>
        <div style={S.rowT}>
          {p.projectName || 'Project not named'}
          {grounded && <span style={S.groundedTag}>on hold</span>}
          {!grounded && p.inTheAir > 0 && (
            <span style={S.airTag}>{p.inTheAir} in the air</span>
          )}
          {!grounded && next && next.blocked && (
            <span style={S.blockedTag} title="Waiting on its own predecessor to post — kept off the flight line until then">blocked</span>
          )}
        </div>
        <div style={S.rowS}>
          {next ? <>next: {next.name}</> : 'nothing queued'}
          {p.openCount > 1 && ` · ${p.openCount} open`}
          {p.overdueCount > 0 && <span style={{ color: PALETTE.red }}> · {p.overdueCount} overdue</span>}
        </div>
      </div>
      <span style={{ ...S.mono, width: 62, textAlign: 'right', color: late ? PALETTE.red : 'var(--aj-text-muted)' }}>
        {next ? fmtDay(next.latestFinish) : '—'}
      </span>
      {/* A queued project's actions live in the modal the row opens — one obvious click rather
          than a second control competing with it. A grounded row opens nothing, so its one
          action has to be right here, and visible. */}
      {grounded
        ? <button style={S.btn} onClick={(e) => { e.stopPropagation(); onHold(p) }}>Resume…</button>
        : <span style={S.chev}>›</span>}
    </div>
  )
}

/**
 * One flight, below the curve, with its checklist.
 *
 * The checklist is shown BESIDE the hill position on purpose: the percentage is the generated
 * signal and the hill is the judged one, and seeing them disagree is the point. A flight sitting
 * at 100% checklist while still uphill is exactly the conversation this board exists to start.
 */
function FlightCard({ f, today, busy, cl, onItem, onLand, onCrew, onFastTrack, hovered, onHoverIn, onHoverOut }) {
  const menu = useActionMenu()

  const late = f.latestFinish && f.latestFinish < today
  const done = cl ? cl.done : null
  const total = cl ? cl.total : null
  const stalled = f.stillDays >= 7

  return (
    <div style={{ ...S.tableRow, ...(hovered ? S.tableRowHover : null), opacity: busy ? 0.5 : 1 }}
      onMouseEnter={onHoverIn} onMouseLeave={onHoverOut}>
      {/* The whole row opens the milestone — a peek into the real outline, not another local
          copy of its checklist (see EntityPeekModal). Navigation is the one unambiguous thing
          a click on the row does; anything else lives in the ⋯ menu. */}
      <div className="fp-fchead" style={S.fcHead} onClick={() => onItem(f)}>
        <Plane deg={attitudeFor(f.hillPosition)} color={stalled ? PALETTE.amber : late ? PALETTE.red : PALETTE.blue} size={22} />
        <span style={S.fcNo}>{flightTag(f)}</span>
        <div className="fp-fcmain" style={S.fcMain}>
          <div style={S.fcTitle}>
            {f.projectName || 'Project not named'} · {f.name}
          </div>
          <div style={S.fcSub}>
            {hillLabel(f.hillPosition)}
            {' · '}
            {f.crew.length ? f.crew.map(c => crewName(c)).join(', ') : <span style={{ color: PALETTE.amber }}>no crew</span>}
            {stalled && <span style={{ color: PALETTE.amber }}> · not moved in {f.stillDays}d</span>}
            {/* Fuel is the phase's own budget health (every flight sharing a phase shares a
                tank) — surfaced here only once it's actually worth flagging; On Track adds
                nothing a manager needs to act on, and it's always visible in the hover card
                and the flight's own row on the curve regardless. EBR-based (2026-09-03) — see
                api/flights.js#computePhaseFuel's own doc comment for why `ebrStatus`, not
                `status`, is the one the gauge reads. */}
            {f.kit && f.kit.preworkOpen > 0 && (
              <span style={{ color: PALETTE.amber }}> · full kit: {f.kit.preworkOpen} open</span>
            )}
            {f.passengers && f.passengers.length > 0 && (
              <span style={{ color: PALETTE.purple }} title={f.passengers.map(p => p.title).join('\n')}> · {f.passengers.length} passenger{f.passengers.length === 1 ? '' : 's'}</span>
            )}
            {f.fuel && f.fuel.ebrStatus !== 'On Track' && (
              <span style={{ color: fuelTone(f.fuel.ebrStatus) }}> · fuel: {f.fuel.ebrFuelLabel.toLowerCase()}</span>
            )}
          </div>
        </div>
        {total != null && (
          <div className="fp-fccl" style={S.fcCl} title="Checklist — the generated number, next to the judged one">
            <span style={S.fcClNum}>{done}/{total}</span>
            <div style={S.fcBar}><i style={{ display: 'block', height: '100%', background: done === total ? PALETTE.green : PALETTE.blue, width: `${total ? (done / total) * 100 : 0}%` }} /></div>
          </div>
        )}
        <span style={{ ...S.mono, color: late ? PALETTE.red : 'var(--aj-text-muted)' }}>{fmtDay(f.latestFinish)}</span>
        <DotsButton menu={menu} title="Flight actions" />
      </div>

      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); onItem(f) }}>Open milestone</ActionMenuItem>
        <ActionMenuItem onClick={() => { menu.close(); onCrew() }}>Change crew</ActionMenuItem>
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => { menu.close(); onLand(f, 'posted') }}>Mark landed</ActionMenuItem>
        {/* Mark landed opens the closeout wizard, which REQUIRES the checklist already complete.
            Fast track completes it for you first — for a flight that turns out to be done in
            reality but never ticked off. Both end at the same post modal and the same gates. */}
        {onFastTrack && (
          <ActionMenuItem onClick={() => { menu.close(); onFastTrack(f) }}
            title="Already done? Complete the checklist and open the post window">
            Fast track to landing
          </ActionMenuItem>
        )}
        <ActionMenuItem onClick={() => { menu.close(); onLand(f, 'released') }}>Pull from the air</ActionMenuItem>
        <ActionMenuItem onClick={() => { menu.close(); onLand(f, 'cancelled') }} danger>Cancel flight</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/**
 * One thread, in the same "In the air" list as flights — Matt: "they also need to show in the
 * in the air list." No checklist body (a Thread doesn't have one) and no crew (see
 * api/thread-hill.js's own doc comment for why) — otherwise the same header-row shape as
 * FlightCard, so the two read as one list rather than two different idioms bolted together.
 */
function ThreadCard({
  th, today, busy, onItem, onPull, onFastTrack, onCrew, hovered, onHoverIn, onHoverOut,
  // Multi-select (2026-09-04, extending Round 30's Next-Up-only mechanism onto flying threads
  // too) — Matt: "allow me to combine flying threads into squadrons." Same shape as
  // QueuedThreadRow's own props; a flying thread and a queued one now check into the exact same
  // `selectedThreadIds` set, so bulkBundleSquadron's existing same-project/2-5-count logic covers
  // a mixed selection with no changes of its own.
  selecting, selected, selectDisabled, selectDisabledReason, onToggleSelect,
}) {
  const menu = useActionMenu()
  const late = th.targetDeadline && th.targetDeadline < today
  const stalled = th.stillDays >= 7
  const crew = th.crew || []
  const handleRowClick = () => {
    if (selecting) { if (!selectDisabled) onToggleSelect(); return }
    onItem(th)
  }

  return (
    <div style={{ ...S.tableRow, ...(hovered ? S.tableRowHover : null), opacity: busy || (selecting && selectDisabled) ? 0.45 : 1, cursor: selecting && selectDisabled ? 'default' : 'pointer' }}
      onMouseEnter={onHoverIn} onMouseLeave={onHoverOut}
      title={selecting && selectDisabled ? selectDisabledReason : undefined}>
      {/* Same "the row opens it" contract as FlightCard above — selecting mode toggles instead. */}
      <div className="fp-fchead" style={S.fcHead} onClick={handleRowClick}>
        {selecting
          ? <input type="checkbox" checked={!!selected} disabled={selectDisabled}
              onChange={onToggleSelect} onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0, width: 15, height: 15, cursor: selectDisabled ? 'default' : 'pointer' }} />
          : <Plane deg={threadAttitudeFor(th.hillPosition)} icon="heli" color={stalled ? PALETTE.amber : late ? PALETTE.red : PALETTE.teal} size={21} />}
        <span style={S.fcNo}>{threadTag(th)}</span>
        <div className="fp-fcmain" style={S.fcMain}>
          <div style={S.fcTitle}>
            {th.projectName || 'Project not named'} · {th.title || 'Untitled thread'}
          </div>
          <div style={S.fcSub}>
            {hillLabel(th.hillPosition)}
            {stalled && <span style={{ color: PALETTE.amber }}> · not moved in {th.stillDays}d</span>}
            {/* Crew (2026-09-01) — a Thread only carries this once someone's been given it as a
                backup assignment (crewThread); most threads still show nothing here, same as
                before. */}
            {crew.length > 0 && <> · {crew.map(c => crewName(c)).join(', ')}</>}
          </div>
        </div>
        {!selecting && <span style={{ ...S.mono, color: late ? PALETTE.red : 'var(--aj-text-muted)' }}>{fmtDay(th.targetDeadline)}</span>}
        {!selecting && <DotsButton menu={menu} title="Thread actions" />}
      </div>

      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); onItem(th) }}>Open thread</ActionMenuItem>
        {onCrew && (
          <ActionMenuItem onClick={() => { menu.close(); onCrew(th) }}>
            {crew.length ? 'Change crew' : 'Crew this thread…'}
          </ActionMenuItem>
        )}
        {onFastTrack && (
          <ActionMenuItem onClick={() => { menu.close(); onFastTrack(th) }}
            title="Close administratively: complete open subtasks + record a Fast-tracked closure">
            Fast track to landing
          </ActionMenuItem>
        )}
        <ActionMenuItem onClick={() => { menu.close(); onPull(th) }} danger>Pull from the curve</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/**
 * One Squadron, in the same "In the air" list — same header-row shape as FlightCard/ThreadCard,
 * but squadron-shaped actions only (no checklist, no "Mark landed"/"Fast track" — a squadron
 * isn't a milestone and doesn't post): crew the whole bundle, release one member thread back to
 * flying solo, or disband. Mirrors OnCurveSquadron's own action menu exactly.
 */
function SquadronCard({ f, busy, onCrew, onLand, onOpenThread, onRemoveThread, onAddThread, hovered, onHoverIn, onHoverOut }) {
  const menu = useActionMenu()
  const members = f.squadronThreads || []
  const stalled = f.stillDays >= 7

  return (
    <div style={{ ...S.tableRow, ...(hovered ? S.tableRowHover : null), opacity: busy ? 0.5 : 1 }}
      onMouseEnter={onHoverIn} onMouseLeave={onHoverOut}>
      <div className="fp-fchead" style={S.fcHead}>
        <Plane deg={0} icon="heli" color={PALETTE.purple} size={21} />
        <span style={S.fcNo}>{f.flightNumber}</span>
        <div className="fp-fcmain" style={S.fcMain}>
          <div style={S.fcTitle}>{f.projectName || 'Project not named'} · {members.length} thread{members.length === 1 ? '' : 's'} bundled</div>
          <div style={S.fcSub}>
            {members.map(m => m.title).filter(Boolean).join(', ') || 'Untitled threads'}
            {stalled && <span style={{ color: PALETTE.amber }}> · not moved in {f.stillDays}d</span>}
            {' · '}
            {f.crew.length ? f.crew.map(c => crewName(c)).join(', ') : <span style={{ color: PALETTE.amber }}>no crew</span>}
          </div>
        </div>
        <DotsButton menu={menu} title="Squadron actions" />
      </div>

      <ActionMenu menu={menu}>
        <ActionMenuItem onClick={() => { menu.close(); onCrew() }}>Crew squadron</ActionMenuItem>
        {members.length < 5 && (
          <ActionMenuItem onClick={() => { menu.close(); onAddThread && onAddThread(f) }}>Add threads…</ActionMenuItem>
        )}
        {members.length > 0 && <ActionMenuDivider />}
        {members.map(m => (
          <ActionMenuItem key={m.threadId} onClick={() => { menu.close(); onOpenThread && onOpenThread(m) }}>
            Open {m.title || 'thread'}
          </ActionMenuItem>
        ))}
        <ActionMenuDivider />
        {members.map(m => (
          <ActionMenuItem key={'rm-' + m.threadId}
            onClick={() => { menu.close(); onRemoveThread && onRemoveThread(f, m) }}>
            Release {m.title || 'thread'} back to flying solo
          </ActionMenuItem>
        ))}
        <ActionMenuDivider />
        <ActionMenuItem onClick={() => { menu.close(); onLand(f, 'released') }} danger>Disband squadron</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/**
 * Why this row sits where it sits (Phase 4 — docs/PRIORITY_ENGINE.md §13).
 *
 * A ranking nobody can interrogate is a ranking nobody trusts, and this one is no longer the
 * due-date sort people could read off the date column themselves. So the two signals that
 * actually MOVE a row against its date get shown next to it: what it unblocks, and how many
 * times it has already been pushed. Both are derived, never typed by anyone.
 *
 * Deliberately not the raw score. The number is a CD3 ratio whose units mean nothing on their
 * own — showing "26.04" invites people to compare magnitudes that were never calibrated for
 * that. What is real and checkable is "this gates 12 other milestones."
 */
function WhyChips({ p }) {
  if (!p) return null
  const chips = []
  if (p.unblocks > 0) chips.push(`gates ${p.unblocks}`)
  if (p.pushes >= 3) chips.push(`pushed ${p.pushes}×`)
  if (p.tier === 'unscored') chips.push('unscored')
  if (!chips.length) return null
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      {chips.map(c => (
        <span key={c} style={{
          font: '500 10px/1.6 "DM Mono", monospace', letterSpacing: '.02em',
          padding: '1px 6px', borderRadius: 20, whiteSpace: 'nowrap',
          background: 'var(--aj-bg-subtle)', color: 'var(--aj-text-muted)',
          border: '0.5px solid var(--aj-border)',
        }}>{c}</span>
      ))}
    </span>
  )
}

/**
 * A waiting milestone in the "Next up" list — the same set FlightDeck's own gate stack shows,
 * as an actual readable row instead of a small icon. Not a flight yet: no checklist, no crew, no
 * ⋯ menu. Clicking the row opens the milestone; "Put in the air" is the exact same crew-picker
 * flow dragging it onto the curve already opens (`onPick` in `MilestonePicker` below).
 */
function QueuedMilestoneRow({ m, today, onOpen, onPutInAir, onFastTrack, busy }) {
  const late = m.latestFinish && m.latestFinish < today
  return (
    <div style={{ ...S.tableRow, opacity: busy ? 0.5 : 1 }}>
      <div className="fp-fchead" style={S.fcHead} onClick={() => onOpen(m)}>
        <Plane deg={0} color={late ? PALETTE.red : 'var(--aj-text-muted)'} size={20} />
        <div className="fp-fcmain" style={S.fcMain}>
          <div style={S.fcTitle}>{m.projectName || 'Project not named'} · {m.name}</div>
          <div style={S.fcSub}>
            {m.phaseName || '—'}
            {/* Full kit (Step 2): the kit state, live from the same crawl the blocked flag reads.
                "Put in the air" works either way; an incomplete kit asks the tower for a reason. */}
            {m.blocked && <BlockedTag m={m} />}
            {m.kit && m.kit.preworkOpen > 0 && (
              <span style={S.kitTag} title="Full-Kit checklist items still open — putting this in the air will ask for a reason">
                full kit {m.kit.preworkOpen} open
              </span>
            )}
            {m.kit && m.kit.ready && !m.blocked && <span style={S.kitOkTag} title="Predecessor posted, full kit complete">kit ready</span>}
          </div>
        </div>
        <WhyChips p={m.priority} />
        <span style={{ ...S.mono, color: late ? PALETTE.red : 'var(--aj-text-muted)' }}>{fmtDay(m.latestFinish)}</span>
        <button style={S.btn} onClick={(e) => { e.stopPropagation(); onPutInAir() }}>Put in the air</button>
        {/* Available WITHOUT crewing, deliberately — Matt: "we have to be able to clear a milestone
            that's actually done and the system can't stop us because there are no available crews.
            That's essential." One-flight-per-person would otherwise make clearing a backlog of
            already-finished work impossible. Same reasoning as thread fast-track from the queue. */}
        {onFastTrack && (
          <button style={S.btn} title="Already done? Complete the checklist and open the post window"
            onClick={(e) => { e.stopPropagation(); onFastTrack(m) }}>
            Fast track
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * A thread still sitting at the gate in the "Next up" list — never been given a judged
 * position (see `isSeedThread`). Clicking the row opens the thread; giving it a position
 * still happens by dragging it off ThreadDeck onto the curve. "Fast track" (2026-08-28) is
 * deliberately available RIGHT HERE, before any launch ceremony — a thread that is already
 * done or dead should never have to be put in the air just to be killed.
 */
function QueuedThreadRow({
  th, today, onOpen, onPutInAir, onFastTrack, busy,
  // Multi-select (2026-09-01) — see the "Next up" select-mode block in FlightPlanTab for the
  // full model. `selectDisabled` covers a targeted add-to-squadron mode where this thread isn't
  // on the target's project or the target is already at its 5-thread cap; `rowStatus` is live
  // feedback ('working'/'done'/an error string) while a bulk run touches this specific thread.
  selecting, selected, selectDisabled, selectDisabledReason, onToggleSelect, rowStatus,
}) {
  const late = th.targetDeadline && th.targetDeadline < today
  const isDone = rowStatus === 'done'
  const isWorking = rowStatus === 'working'
  const isRowError = rowStatus && !isDone && !isWorking
  const handleRowClick = () => {
    if (selecting) { if (!selectDisabled) onToggleSelect(); return }
    onOpen(th)
  }
  return (
    <div
      style={{ ...S.tableRow, cursor: selectDisabled ? 'default' : 'pointer', opacity: busy || isDone || (selecting && selectDisabled) ? 0.45 : 1 }}
      onClick={handleRowClick}
      title={selecting && selectDisabled ? selectDisabledReason : undefined}
    >
      <div className="fp-fchead" style={S.fcHead}>
        {selecting
          ? <input type="checkbox" checked={!!selected} disabled={selectDisabled || isDone}
              onChange={onToggleSelect} onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0, width: 15, height: 15, cursor: selectDisabled ? 'default' : 'pointer' }} />
          : <Plane deg={0} icon="heli" color={late ? PALETTE.red : PALETTE.teal} size={19} />}
        <div className="fp-fcmain" style={S.fcMain}>
          <div style={S.fcTitle}>{th.projectName || 'Project not named'} · {th.title || 'Untitled thread'}</div>
          <div style={S.fcSub}>{selecting ? (th.type || 'Thread') : 'Waiting for a judged position'}</div>
        </div>
        {!selecting && <span style={{ ...S.mono, color: late ? PALETTE.red : 'var(--aj-text-muted)' }}>{fmtDay(th.targetDeadline)}</span>}
        {selecting && isWorking && <span style={S.bulkState}>working…</span>}
        {selecting && isDone && <span style={{ ...S.bulkState, color: PALETTE.green }}>✓ done</span>}
        {selecting && isRowError && <span style={{ ...S.bulkState, color: PALETTE.red }} title={rowStatus}>failed</span>}
        {/* Both controller choices, side by side (Matt: "we should have two options for items
            not yet in flight: Put in the Air and Fast Track"). Put in the air = the same
            judged-position move dragging onto the curve makes — no note prompt for THIS one
            move (see moveThreadHill's own comment), only for whatever comes after. Hidden while
            selecting — a single-item action reads as a distraction mid-bulk-pick. */}
        {!selecting && onPutInAir && (
          <button style={S.btn} title="Give it a takeoff position on the curve — no explanation needed for this first move"
            onClick={(e) => { e.stopPropagation(); onPutInAir(th) }}>
            Put in the air
          </button>
        )}
        {!selecting && onFastTrack && (
          <button style={S.btn} title="Close administratively: complete open subtasks + record a Fast-tracked closure"
            onClick={(e) => { e.stopPropagation(); onFastTrack(th) }}>
            Fast track
          </button>
        )}
      </div>
    </div>
  )
}

const first = (v) => (Array.isArray(v) ? v[0] : v)

/** Who owes a feeder, lower-case for use mid-sentence (docs/OPERATING_MODEL.md step 2). */
const FEEDER_OWED_BY = { agency: 'the City', consultant: 'a consultant', client: 'the client', gc: 'the GC' }

/** The "blocked" tag, naming what it is waiting on (rule 10: nothing hidden without a reason). */
function BlockedTag({ m }) {
  const names = (m.waitingOn && m.waitingOn.length) ? m.waitingOn : null
  return (
    <span style={S.blockedTag} title={names ? 'Waiting on ' + names.join(' and ') + ' to post' : 'Waiting on its own predecessor to post'}>
      {names ? 'waiting on ' + names.join(', ') : 'blocked'}
    </span>
  )
}

/**
 * "Not in Next up": every queued milestone the list above leaves out, grouped by the one reason
 * it was left out (docs/OPERATING_MODEL.md rule 10, 2026-09-23). "Put in the air" still works from
 * here: a milestone waiting on a predecessor can be started early, exactly as from the picker.
 */
function HiddenQueue({ hidden, today, onOpen, onPutInAir }) {
  if (!hidden || !hidden.length) return null
  const groups = [
    { key: 'waiting', title: 'Waiting on another milestone to post', sub: m => 'waiting on ' + m.hiddenDetail },
    { key: 'behind', title: 'Behind their project’s next milestone', sub: m => 'after ' + m.hiddenDetail + ' (only each project’s next shows above; pick the project to see its whole line)' },
  ]
  return (
    <Collapsible title="Not in Next up" meta={`${hidden.length} open milestone${hidden.length === 1 ? '' : 's'}, and why`}>
      {groups.map(g => {
        const rows = hidden.filter(m => m.hiddenReason === g.key)
        if (!rows.length) return null
        return (
          <div key={g.key} style={{ padding: '8px 0' }}>
            <div style={S.hiddenGroupHead}>{g.title} <span style={S.sectN}>{rows.length}</span></div>
            {rows.map(m => {
              const late = m.latestFinish && m.latestFinish < today
              return (
                <div key={m.milestoneId} style={S.row}>
                  <div style={S.rowMain}>
                    <div style={S.rowT} onClick={() => onOpen(m)} title={m.name}>{m.projectName || 'Project not named'} · {m.name}</div>
                    <div style={S.rowS}>{g.sub(m)}</div>
                  </div>
                  <span style={{ ...S.mono, color: late ? PALETTE.red : 'var(--aj-text-muted)' }}>{fmtDay(m.latestFinish)}</span>
                  <button style={S.btn} onClick={() => onPutInAir(m)}
                    title={g.key === 'waiting' ? 'Its predecessor has not posted yet. This still works if you mean to start early.' : undefined}>
                    Put in the air
                  </button>
                </div>
              )
            })}
          </div>
        )
      })}
    </Collapsible>
  )
}

/** A project's own upcoming milestones. Pick one to put on a runway, or ground the project. */
function MilestonePicker({ project, today, onClose, onPick, onItem, onHold }) {
  return (
    <div style={S.backdrop} onClick={onClose}>
      <div style={{ ...S.modal, width: 'min(560px, 92vw)' }} onClick={e => e.stopPropagation()}>
        <div style={S.modalHead}>{project.projectName || 'Project not named'}</div>
        <div style={S.modalSub}>
          {project.openCount} open milestone{project.openCount === 1 ? '' : 's'}
          {project.overdueCount > 0 && `, ${project.overdueCount} overdue`}
          . Pick one to put in the air.
        </div>
        <div style={S.modalList}>
          {project.milestones.map(m => {
            const late = m.latestFinish && m.latestFinish < today
            return (
              <div key={m.milestoneId} style={{ ...S.pickRow, opacity: m.blocked ? 0.6 : 1 }}>
                <div style={S.rowMain}>
                  <div style={S.rowT} onClick={() => onItem(m)}>
                    {m.name}
                    {m.blocked && <BlockedTag m={m} />}
                    {/* Full kit (Step 2): the checklist half of the kit, live from the same crawl
                        the blocked tag reads. Launching with either open asks for a reason. */}
                    {m.kit && m.kit.preworkOpen > 0 && (
                      <span style={S.kitTag} title="Full-Kit checklist items still open — putting this in the air will ask for a reason">
                        full kit {m.kit.preworkOpen} open
                      </span>
                    )}
                    {m.kit && m.kit.ready && !m.blocked && <span style={S.kitOkTag} title="Predecessor posted, full kit complete">kit ready</span>}
                  </div>
                  <div style={S.rowS}>
                    {m.phaseName || '—'}
                    {m.daysBudget != null && m.daysBudget > 0 && ` · ${fmtDays(m.daysBudget)} budgeted`}
                    {m.progress != null && m.progress > 0 && ` · ${Math.round(m.progress * 100)}%`}
                    {m.owner && ` · ${m.owner}`}
                  </div>
                </div>
                <span style={{ ...S.mono, width: 62, textAlign: 'right', color: late ? PALETTE.red : 'var(--aj-text-muted)' }}>
                  {fmtDay(m.latestFinish)}
                </span>
                <button
                  style={S.btn} onClick={() => onPick(m)}
                  title={m.blocked ? 'Its predecessor has not posted yet — this still works if you mean to start early' : undefined}
                >Put in the air</button>
              </div>
            )
          })}
        </div>
        <div style={S.modalFoot}>
          {/* Grounding belongs here, not only on the row behind this modal — this is where you
              are actually looking at the project and deciding it is going nowhere. */}
          <button style={S.btn} onClick={() => onHold(project)}>Ground this project…</button>
          <div style={{ flex: 1 }} />
          <button style={S.btn} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────────────────────
   Crew picker
   ───────────────────────────────────────────────────────────────────────────── */

// A person may crew up to 2 things at once (database/204_crew_two_and_thread_crew.sql, Matt
// 2026-09-01: "there will be times when a crew member is waiting on something and can have a
// backup item to work on"). Any pairing works — the trigger only counts open flight_crew rows,
// blind to kind — so this is a plain constant, not a per-kind rule.
const MAX_SIMULTANEOUS_ASSIGNMENTS = 2

function CrewPicker({ target, team, roleSkills, crewLoad, maxCrew, flightById, threadById, onClose, onSave }) {
  // Three target shapes now: {milestone} (not yet a flight), {flight} (an existing milestone or
  // squadron flight), {thread} (2026-09-01 — a bare thread, itself crewed only if it already
  // carries a crewFlightId from a prior crewThread call; a never-crewed thread has none).
  const currentFlightId = target.flight ? target.flight.flightId : (target.thread ? target.thread.crewFlightId : null)
  const existing = (target.flight && target.flight.crew) || (target.thread && target.thread.crew) || []
  const [picked, setPicked] = useState(() => existing.map(c => c.memberAirtableId))
  const [captain, setCaptain] = useState(() => (existing.find(c => c.isCaptain) || {}).memberAirtableId || null)
  // PRIORITY per person (2026-09-10, Matt): each seat is this person's FIRST or SECOND plane. Required.
  // Defaults to whichever rank their other plane is not using; picking the taken rank swaps the two.
  const [prio, setPrio] = useState(() => Object.fromEntries(existing.filter(c => c.priority).map(c => [c.memberAirtableId, c.priority])))
  const ms = target.milestone || target.flight || target.thread
  const msName = ms && (ms.name || ms.title)
  const cap = maxCrew || 2

  // ── SKILL FIT (Phase 4 — docs/PRIORITY_ENGINE.md §13) ───────────────────────────────────
  // Order the roster by who actually fits this milestone instead of alphabetically, and say
  // which skill matched so the suggestion is arguable rather than oracular.
  //
  // Deliberately NOT eligibility.js#rankCandidates, despite §3 calling this "mostly wiring."
  // That function reconciles skill fit with WORKLOAD (`p.metrics.status`, `loadRatio`) and drops
  // anyone lacking a metrics object — this roster has none, so it would return an empty list.
  // More to the point, its availability half would be redundant here: the crew system already
  // answers "is this person free" far better than a workload heuristic can, as a hard database
  // constraint, and `busyOn`/`atCapacity` below already grey those people out. So this uses the
  // SKILL primitives only and lets crewLoad keep owning availability. A thread's own `.type`
  // (RFI/Submittal/…) never matches a milestone-deliverable-type skill map — fit is simply null
  // for a thread target, same as any milestone type nobody has curated skills for.
  const fit = useMemo(() => {
    const req = requiredSkills(msName)
    if (!req.length || !roleSkills) return null
    const by = {}
    for (const t of (team || [])) {
      const skills = personSkills(t, { roleSkills })
      const matched = req.filter(s => skills[s])
      by[t.id] = { matched, score: matched.reduce((n, s) => n + skills[s], 0) }
    }
    return by
  }, [msName, team, roleSkills])

  // Sorted copy — never mutate the roster prop in place. Unmatched people keep alphabetical
  // order below the matched ones rather than being hidden: the tower can always crew whoever it
  // wants, and a skill map that is merely incomplete must never remove someone from the list.
  const roster = useMemo(() => {
    if (!fit) return team || []
    return (team || []).slice().sort((a, b) =>
      ((fit[b.id] || {}).score || 0) - ((fit[a.id] || {}).score || 0)
      || String(a.name || '').localeCompare(String(b.name || '')))
  }, [team, fit])

  // Up to 2 simultaneous assignments now, not 1 — someone already at capacity is unpickable
  // here; someone with exactly one OTHER assignment is still pickable, just shown what else
  // they're on. Their seat on THIS flight/thread obviously never counts against themselves.
  const otherAssignments = (id) => ((crewLoad || {})[id] || []).filter(a => a.flightId !== currentFlightId)
  // flightTag() prepends the project code (e.g. "DHSR-CFW101") — Matt: "show full flight number
  // (i.e., project)" here, since a bare number like "PHP102" doesn't say which project someone
  // else is already on. crewLoad entries carry projectName precisely so this can call flightTag
  // directly instead of reading flightNumber alone.
  //
  // A THREAD-crew assignment (kind: 'thread') gets the real thing instead — threadTag(), the
  // same real Airtable "Thread Number" every other thread display now shows (2026-09-03), not
  // the internal THR### capacity-accounting sequence. crewLoad entries carry no threadId of
  // their own, so this resolves flightId → the flight row (flightById, for its threadId) → the
  // real thread object (threadById) — the same two-hop join FlightPlanTab's own
  // describeAssignment already does for the crew popover's ticket table.
  const busyOn = (id) => {
    const others = otherAssignments(id)
    return others.length ? others.map(a => {
      const flight = flightById && flightById.get(a.flightId)
      const th = (flight && flight.kind === 'thread' && threadById) ? threadById.get(flight.threadId) : null
      return th ? threadTag(th) : flightTag(a)
    }).join(', ') : null
  }
  const atCapacity = (id) => otherAssignments(id).length >= MAX_SIMULTANEOUS_ASSIGNMENTS

  const heldRank = (id) => { const o = otherAssignments(id).find(a => a.priority === 1 || a.priority === 2); return o ? o.priority : null }
  const defaultRank = (id) => (heldRank(id) === 1 ? 2 : 1)
  const rankOf = (id) => prio[id] || defaultRank(id)

  const toggle = (id) => setPicked(p => {
    if (p.includes(id)) {
      const next = p.filter(x => x !== id)
      if (captain === id) setCaptain(null)
      return next
    }
    if (p.length >= cap) return p        // a crew is a captain and a first officer
    setPrio(x => ({ ...x, [id]: x[id] || defaultRank(id) }))
    return p.concat(id)
  })

  // ONE CAPTAIN, ALWAYS (database/238, 2026-09-11): a lone pilot is the captain, no choice offered;
  // with two aboard the tower may pick, and if it doesn't the database names one (whose first plane
  // this is, then the earlier seat). Save never waits on the captain button.
  const effectiveCaptain = picked.length === 1 ? picked[0] : captain
  const save = () => onSave(picked.map(id => ({
    memberAirtableId: id,
    memberName: (team.find(t => t.id === id) || {}).name || '',
    isCaptain: id === effectiveCaptain,
    priority: rankOf(id),
  })))

  return (
    <div style={S.backdrop} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.modalHead}>
          {target.flight ? `Crew ${target.flight.flightNumber}`
            : target.thread ? (target.thread.crewFlightId ? 'Change thread crew'
              : target.hill != null ? 'Put in the air' : 'Crew this thread')
            : target.squadron ? 'Crew this squadron'
            : 'Put in the air'}
        </div>
        <div style={S.modalSub}>
          {target.squadron
            ? `${target.squadron.threads.length} threads · ${target.squadron.threads[0].projectName || 'Project not named'}`
            : <>{ms.projectName || 'Project not named'} · {msName}</>}
          {!target.flight && !(target.thread && target.thread.crewFlightId) && (
            <div style={{ marginTop: 4 }}>
              {target.squadron
                ? 'The squadron is created only once you confirm a crew.'
                : target.thread
                  ? (target.hill != null
                    ? 'A flight number is assigned when you confirm.'
                    : 'This is a backup assignment, not a launch — the thread stays right where it is.')
                  : 'A flight number is assigned when you confirm.'}
            </div>
          )}
        </div>
        <div style={S.modalList}>
          {team.length === 0 && <div style={S.empty}>Loading the team…</div>}
          {roster.map(t => {
            const on = picked.includes(t.id)
            const busy = busyOn(t.id)
            const full = !on && picked.length >= cap
            const disabled = (!on && atCapacity(t.id)) || full
            return (
              <div key={t.id} style={{ ...S.crewRow, opacity: disabled ? 0.45 : 1 }}>
                <label style={{ ...S.crewLabel, cursor: disabled ? 'default' : 'pointer' }}>
                  <input type="checkbox" checked={on} disabled={disabled} onChange={() => toggle(t.id)} />
                  <span>{t.name}</span>
                  {fit && (fit[t.id] || {}).matched && fit[t.id].matched.length > 0 && (
                    <span style={{
                      font: '500 10px/1.6 "DM Mono", monospace', padding: '1px 6px',
                      borderRadius: 20, background: 'var(--aj-bg-subtle)',
                      color: 'var(--aj-text-muted)', border: '0.5px solid var(--aj-border)',
                    }} title="Matched from this person's Practice Role">
                      {fit[t.id].matched.join(' + ')}
                    </span>
                  )}
                  {/* Still pickable with one backup slot used (informational tag) vs. already
                      full (the disabling tag) read differently on purpose — "also on X" invites
                      the pick, "full — on X, Y" explains why it's greyed out. */}
                  {busy && (
                    <span style={disabled ? S.busyTag : S.busyTagInfo}>
                      {disabled ? `full — on ${busy}` : `also on ${busy}`}
                    </span>
                  )}
                </label>
                {on && (
                  <>
                    {[1, 2].map(r => {
                      const otherHeld = heldRank(t.id) === r
                      return (
                        <button key={r}
                          style={{ ...S.capBtn, ...(rankOf(t.id) === r ? S.prioOn : null) }}
                          onClick={() => setPrio(x => ({ ...x, [t.id]: r }))}
                          title={r === 1 ? 'Their first-priority plane' : 'Their second-priority plane'}
                        >{r === 1 ? '1st' : '2nd'}{otherHeld && rankOf(t.id) === r ? ' · swaps' : ''}</button>
                      )
                    })}
                    <button
                      style={{ ...S.capBtn, ...(effectiveCaptain === t.id ? S.capOn : null), ...(picked.length === 1 ? { cursor: 'default' } : null) }}
                      onClick={() => { if (picked.length === 1) return; setCaptain(captain === t.id ? null : t.id) }}
                      title={picked.length === 1 ? 'The only pilot aboard is the captain' : 'A flight crew is a captain and a first officer, and both are crew'}
                    >captain</button>
                  </>
                )}
              </div>
            )
          })}
        </div>
        <div style={S.modalFoot}>
          <span style={{ fontSize: 11, color: 'var(--aj-text-muted)' }}>
            {picked.length} of {cap} crewed{picked.length >= 2 && !captain ? ' · no captain picked — whoever calls this their 1st plane takes it' : ''}
          </span>
          <div style={{ flex: 1 }} />
          <button style={S.btn} onClick={onClose}>Cancel</button>
          <button style={{ ...S.btn, ...S.btnPrimary }} onClick={save} disabled={picked.length === 0}>
            {target.flight ? 'Save crew' : 'Assign flight number'}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────────────────────────────────────────
   Bits
   ───────────────────────────────────────────────────────────────────────────── */

/**
 * A VISIBLE actions trigger.
 *
 * Deliberately not `ActionMenuButton` from ActionMenu.jsx. That is the outline engine's control:
 * outline.css gives `.wf-menu-btn` `opacity: 0` and `grid-area: ell`, revealing it only on
 * `.wf-row:hover`. This board's rows are plain flex containers, not `.wf-row`, so every one of
 * those buttons rendered into the DOM and stayed permanently invisible — which is exactly how
 * "Mark in flight" and "Ground this project" both ended up unreachable.
 *
 * Hover-to-discover is the wrong idiom here regardless. This is the board a principal opens
 * every morning; its controls should be visible without hunting.
 */
function DotsButton({ menu, title }) {
  return (
    <button
      style={S.dots} title={title || 'Actions'}
      onClick={(e) => { e.stopPropagation(); menu.toggle(e.currentTarget) }}
    >⋯</button>
  )
}

/** A full-width section that folds. Used for Grounded and Holding. */
function Collapsible({ title, meta, defaultOpen, children }) {
  const [open, setOpen] = useState(!!defaultOpen)
  return (
    <div>
      <button style={S.collapseHead} onClick={() => setOpen(o => !o)}>
        <span style={{ ...S.caret, transform: open ? 'rotate(90deg)' : 'none' }}>›</span>
        <span style={S.collapseTitle}>{title}</span>
        {meta && <span style={S.sectN}>{meta}</span>}
      </button>
      {open && <div style={{ ...S.card, padding: '2px 14px' }}>{children}</div>}
    </div>
  )
}

const SectionHead = ({ title, meta, action }) => (
  // fp-secth: compact-only wrap (the Next Up head carries a whole bulk-action button cluster) —
  // see the media block in FlightPlanTab's own <style>. No effect above the breakpoint.
  <div className="fp-secth" style={S.sectH}>
    {title}{meta && <span style={S.sectN}>{meta}</span>}
    {action && <><div style={{ flex: 1 }} />{action}</>}
  </div>
)

/** Milestones / threads / both — the "Next up" list filter. Briefly labeled "Planes" /
 * "Helicopters" (matching the icons); Matt asked for the entity names instead, 2026-08-31. IDs
 * stay 'flights'/'threads' — they're wired into the filter logic, this only changed the label. */
const QUEUE_FILTER_OPTIONS = [
  { id: 'both', label: 'Both' },
  { id: 'flights', label: 'Milestones' },
  { id: 'threads', label: 'Threads' },
]
const QueueFilterToggle = ({ value, onChange }) => (
  <div style={S.filterToggle}>
    {QUEUE_FILTER_OPTIONS.map((o, i) => (
      <button key={o.id}
        style={{
          ...S.filterToggleBtn,
          ...(i < QUEUE_FILTER_OPTIONS.length - 1 ? S.filterToggleBtnBorder : null),
          ...(value === o.id ? S.filterToggleBtnOn : null),
        }}
        onClick={() => onChange(o.id)}
      >
        {o.label}
      </button>
    ))}
  </div>
)

const Stat = ({ n, label, color }) => (
  <div><div style={{ ...S.statN, color: color || 'var(--aj-text)' }}>{n}</div><div style={S.statL}>{label}</div></div>
)

// Plane is imported from FlightTracker.jsx now (it grew an `icon` prop for the threads
// helicopter, and the two symbol defs it references, <PlaneDefs/>, are already rendered once by
// FlightTracker itself — <use href="#id"> resolves against the whole document, not just its own
// subtree, so this works fine here below the curve too).


/**
 * THE LEGS OF THE FLIGHT — the Tower mockup's legs panel, on the real board (Matt, 2026-09-07: "a
 * left side terminal list… show every project without having to expand anything… chips that stand
 * out like they do in the Alder James Tower mockup").
 *
 * A project is a plane; the trip is cut into legs (groups of phases); a leg holds so many planes at
 * once. Every live project is on this strip exactly once: as a FLYING chip in the leg where one of
 * its milestones is crewed right now (these are what the limit counts and what the rope refuses
 * against), as a WAITING chip behind the leg its next milestone lands in (open work, nobody flying
 * it — muted), or at the TERMINAL on the left when its next step maps to no leg. The number on each
 * leg is editable here; that visibility is the escape hatch.
 */
const shortName = (n) => String(n || '')
  .replace(/ Residence.*$| Remodel.*$| Second Story.*$| Retaining.*$| Capistrano.*$| 79 S.*$| Upgrades$| ADU$| and Addition$/, '')
  .split(' ').slice(0, 3).join(' ')

function LegChip({ name, title, light, tag, muted }) {
  return (
    <div style={{ ...L.pj, ...(muted ? L.pjMuted : null) }} title={title || name}>
      <span style={{ ...L.light, background: light }} />
      <span style={L.pjName}>{shortName(name)}</span>
      {tag ? <small style={L.pjTag}>{tag}</small> : null}
    </div>
  )
}

export function LegsStrip({ legs, terminal, busy, onHolds }) {
  if (!legs.length) return null
  return (
    <div style={L.wrap}>
      <div style={L.title}>The legs of the flight</div>
      <div style={L.sub}>Every project, once. Bright chips are flying: someone is crewed on a milestone there, and those are what a leg counts. Grey chips are waiting behind that leg with open work and nobody flying it. A full leg refuses new planes until one moves on; the number is the limit, and changing it here is the visible way around it.</div>
      <div style={L.row}>
        <div style={{ ...L.leg, ...L.door }}>
          <div style={L.legHead}><span style={L.legName}>Terminal</span><span style={L.count}>{(terminal || []).length}</span></div>
          <div style={L.who}>Signed, next step not on a leg yet.</div>
          <div style={L.slots}>
            {(terminal || []).map(t => <LegChip key={t.projectId} name={t.projectName} title={`${t.projectName}${t.next ? ' · next: ' + t.next : ''}`} light={t.overdue ? PALETTE.red : 'var(--aj-border-mid)'} muted />)}
          </div>
        </div>
        {legs.map(l => {
          const limited = l.holds != null
          const n = l.count
          const pill = !limited ? { text: `no limit · ${n}`, style: null }
            : l.over ? { text: `${n - l.holds} too many`, style: L.pillOver }
            : l.full ? { text: 'full', style: L.pillFull }
            : { text: `${l.holds - n} open`, style: L.pillOpen }
          return (
            <div key={l.key} style={{ ...L.leg, borderColor: l.over ? 'rgba(242,85,90,0.55)' : (l.full ? 'rgba(245,166,35,0.55)' : 'var(--aj-border)') }} title={l.description || ''}>
              <div style={L.legHead}>
                <span style={L.legName}>{l.name}</span>
                <span style={{ ...L.pill, ...(pill.style || {}) }}>{pill.text}</span>
              </div>
              <div style={L.limit}>
                {limited
                  ? <>
                      <button style={L.pm} disabled={busy} aria-label="Fewer" onClick={() => onHolds(l.key, Math.max(0, l.holds - 1))}>−</button>
                      <span style={L.n}>{l.holds}</span>
                      <button style={L.pm} disabled={busy} aria-label="More" onClick={() => onHolds(l.key, l.holds + 1)}>+</button>
                      <span>at a time</span>
                    </>
                  : <span>no limit on this leg</span>}
              </div>
              <div style={L.slots}>
                {l.planes.map(p => (
                  <LegChip key={p.projectId} name={p.projectName} light={PALETTE.blue}
                    tag={p.flights.map(f => f.flightNumber).join(' ')}
                    title={p.projectName + ' — ' + p.flights.map(f => `${f.flightNumber} ${f.milestoneName}${f.crew.length ? ' · ' + f.crew.join(', ') : ''}`).join('; ')} />
                ))}
                {limited && n < l.holds && Array.from({ length: l.holds - n }).map((_, i) => <div key={'slot' + i} style={{ ...L.pj, ...L.pjEmpty }}>open slot</div>)}
                {(l.waiting || []).map(w => (
                  <LegChip key={w.projectId} name={w.projectName} light={w.overdue ? PALETTE.red : 'var(--aj-border-mid)'} muted
                    title={`${w.projectName} · waiting · next: ${w.next}${w.overdue ? ' · has overdue work' : ''}`} />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const L = {
  wrap: { background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: '12px 14px' },
  title: { fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--aj-text-muted)' },
  sub: { fontSize: 12.5, color: 'var(--aj-text-mid)', margin: '3px 0 10px', maxWidth: 820, lineHeight: 1.45 },
  row: { display: 'grid', gridTemplateColumns: '130px repeat(6, 1fr)', gap: 10, alignItems: 'stretch' },
  leg: { background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: '10px 10px 12px', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 150, minWidth: 0 },
  door: { background: 'transparent', borderStyle: 'dashed' },
  legHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 },
  legName: { fontWeight: 700, fontSize: 13.5, color: 'var(--aj-text)' },
  who: { fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.3 },
  count: { fontFamily: '"DM Mono", monospace', fontSize: 12, color: 'var(--aj-text-muted)' },
  pill: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, letterSpacing: '.06em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 999, background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', color: 'var(--aj-text-muted)', whiteSpace: 'nowrap' },
  pillOpen: { background: 'rgba(29,158,117,0.13)', color: '#1D9E75', borderColor: 'transparent' },
  pillFull: { background: 'rgba(245,166,35,0.15)', color: '#B7651A', borderColor: 'transparent' },
  pillOver: { background: 'rgba(242,85,90,0.12)', color: PALETTE.red, borderColor: 'transparent' },
  limit: { display: 'flex', alignItems: 'center', gap: 6, fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: 'var(--aj-text-mid)' },
  n: { minWidth: 14, textAlign: 'center' },
  pm: { font: 'inherit', width: 20, height: 20, lineHeight: 1, padding: 0, borderRadius: '50%', border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer', fontSize: 12 },
  slots: { display: 'flex', flexDirection: 'column', gap: 6, flex: 1 },
  pj: { background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', borderRadius: 8, padding: '7px 9px', fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--aj-text)', minWidth: 0 },
  pjMuted: { fontWeight: 500, color: 'var(--aj-text-mid)', background: 'transparent', borderColor: 'var(--aj-border)' },
  pjEmpty: { borderStyle: 'dashed', color: 'var(--aj-text-muted)', fontWeight: 500, justifyContent: 'center' },
  pjName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pjTag: { color: 'var(--aj-text-muted)', fontWeight: 500, marginLeft: 'auto', fontFamily: '"DM Mono", monospace', fontSize: 10.5, whiteSpace: 'nowrap' },
  light: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0, display: 'inline-block' },
}

/* Every colour is a var(--aj-*) token so the tab is dark-mode correct with no JS branching. */
const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 20 },
  filler: { padding: '40px 0', textAlign: 'center', fontSize: 13, color: 'var(--aj-text-muted)' },
  error: { padding: '10px 14px', borderRadius: 10, fontSize: 12.5, color: PALETTE.red, border: '0.5px solid rgba(242,85,90,0.35)', background: 'rgba(242,85,90,0.06)' },
  warn: { padding: '10px 14px', borderRadius: 10, fontSize: 12.5, color: '#B7651A', border: '0.5px solid rgba(245,166,35,0.4)', background: 'rgba(245,166,35,0.08)' },
  busyTag: { fontSize: 10, color: 'var(--aj-text-muted)', fontFamily: '"DM Mono", monospace' },
  // Same shape as busyTag, but for someone who still HAS a pickable second slot — informational,
  // not a reason they're greyed out. A touch of blue keeps it from reading as a warning.
  busyTagInfo: { fontSize: 10, color: PALETTE.blue, fontFamily: '"DM Mono", monospace' },
  groundedTag: { marginLeft: 8, fontSize: 10, fontWeight: 500, color: PALETTE.amber, border: '0.5px solid rgba(245,166,35,0.45)', borderRadius: 20, padding: '1px 7px' },
  airTag: { marginLeft: 8, fontSize: 10, fontWeight: 500, color: PALETTE.blue, background: 'rgba(61,125,255,0.12)', borderRadius: 20, padding: '1px 7px' },
  blockedTag: { marginLeft: 8, fontSize: 10, fontWeight: 500, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-muted)', borderRadius: 20, padding: '1px 7px' },
  kitTag: { marginLeft: 8, fontSize: 10, fontWeight: 600, color: PALETTE.amber, background: 'rgba(184,84,10,0.10)', borderRadius: 20, padding: '1px 7px', fontFamily: '"DM Mono", monospace' },
  kitOkTag: { marginLeft: 8, fontSize: 10, fontWeight: 500, color: 'var(--aj-text-muted)', border: '0.5px solid var(--aj-border)', borderRadius: 20, padding: '0 7px' },
  chev: { fontSize: 15, color: 'var(--aj-text-muted)', lineHeight: 1 },

  collapseHead: { display: 'flex', alignItems: 'baseline', gap: 8, width: '100%', padding: '4px 0 9px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' },
  collapseTitle: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--aj-text-muted)' },
  caret: { fontSize: 13, lineHeight: 1, color: 'var(--aj-text-muted)', transition: 'transform 160ms ease', display: 'inline-block' },

  fcHead: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer' },
  fcNo: { fontFamily: '"DM Mono", monospace', fontSize: 12.5, fontWeight: 500, color: 'var(--aj-text)', flex: '0 0 auto' },
  fcMain: { flex: '1 1 auto', minWidth: 0 },
  fcTitle: { fontSize: 12.5, color: 'var(--aj-text)', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fcSub: { fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fcCl: { display: 'flex', flexDirection: 'column', gap: 3, width: 92, flex: '0 0 auto', cursor: 'pointer' },
  fcClNum: { fontFamily: '"DM Mono", monospace', fontSize: 10, color: 'var(--aj-text-muted)', textAlign: 'right' },
  fcBar: { height: 3, borderRadius: 2, background: "var(--aj-bg-muted)", overflow: "hidden" },
  sortBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, textTransform: 'none', letterSpacing: 0, color: 'var(--aj-text-mid)', background: 'transparent', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, padding: '3px 10px', cursor: 'pointer' },
  dots: { fontFamily: '"DM Sans", sans-serif', fontSize: 15, fontWeight: 700, lineHeight: 1, letterSpacing: '-1px', color: 'var(--aj-text-mid)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '3px 9px', cursor: 'pointer' },
  pickRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '0.5px solid var(--aj-border)' },


  stats: { display: 'flex', alignItems: 'flex-end', gap: 26, padding: '12px 16px', border: '0.5px solid var(--aj-border)', borderRadius: 10, background: 'var(--aj-bg-subtle)' },
  statN: { fontFamily: '"DM Mono", monospace', fontSize: 19, lineHeight: 1.1 },
  statL: { fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 2 },

  sectH: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--aj-text-muted)', display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 9 },
  sectN: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, fontWeight: 400, letterSpacing: 0 },

  card: { background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: '12px 16px 0', position: 'relative' },
  // A list of rows read as one table, not a stack of floating cards — Matt: "bring the cards
  // together into more of a table so there's not spacing between the rows." One shared border/
  // radius on the wrapper; each row (FlightCard/ThreadCard/QueuedMilestoneRow/QueuedThreadRow)
  // draws its own bottom divider instead of its own card chrome.
  tableWrap: { background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border)', borderRadius: 10, overflow: 'hidden' },
  tableRow: { borderBottom: '0.5px solid var(--aj-border)', transition: 'background-color 120ms ease' },
  // Paired with FlightTracker's own "glow" (2026-09-03) — hovering a row here highlights it AND
  // grows/glows the matching on-curve icon, so the two directions of "where is this" read as one
  // feature. var(--aj-bg-subtle) is the same theme-aware hover/zebra tone used elsewhere in the app.
  tableRowHover: { background: 'var(--aj-bg-subtle)' },
  cols: { display: 'grid', gridTemplateColumns: 'minmax(0, 62fr) minmax(0, 38fr)', gap: 24 },

  axis: { position: 'relative', height: 18, borderBottom: '0.5px solid var(--aj-border)' },
  tick: { position: 'absolute', top: 0, fontFamily: '"DM Mono", monospace', fontSize: 10, color: 'var(--aj-text-muted)', transform: 'translateX(-50%)' },
  now: { position: 'absolute', top: 0, bottom: 0, width: 1, background: 'var(--aj-text)', opacity: 0.55 },
  thresh: { position: 'absolute', top: 0, bottom: 0, width: 9, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '3px 0' },
  threshMark: { display: 'block', height: 3, background: 'var(--aj-text)', opacity: 0.16 },

  noFlights: { padding: '26px 0 26px 14px', fontSize: 12, color: 'var(--aj-text-muted)', fontStyle: 'italic' },
  lane: { display: 'flex', alignItems: 'stretch', borderBottom: '0.5px solid var(--aj-border)', position: 'relative' },
  laneLabel: { padding: '10px 12px 10px 0' },
  laneTop: { display: 'flex', alignItems: 'center', gap: 6 },
  flightNo: { fontFamily: '"DM Mono", monospace', fontSize: 13, fontWeight: 500, letterSpacing: '0.02em' },
  laneActions: { display: 'flex', alignItems: 'center', paddingLeft: 6, flex: '0 0 auto' },
  laneWho: { fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 3, lineHeight: 1.35 },
  laneTrack: { position: 'relative', flex: '1 1 auto', background: 'rgba(127,127,127,0.035)', minHeight: 58 },

  flight: { position: 'absolute', top: '50%', display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' },
  flightT: { fontSize: 11.5, lineHeight: 1.25, cursor: 'pointer', color: 'var(--aj-text)' },
  flightS: { fontFamily: '"DM Mono", monospace', fontSize: 10, color: 'var(--aj-text-muted)' },

  legend: { display: 'flex', alignItems: 'center', gap: 18, padding: '9px 0 10px', marginTop: 4, borderTop: '0.5px solid var(--aj-border)', fontSize: 10.5, color: 'var(--aj-text-muted)', flexWrap: 'wrap' },
  legendItem: { display: 'inline-flex', alignItems: 'center', gap: 7 },

  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '0.5px solid var(--aj-border)' },
  rowMain: { flex: '1 1 auto', minWidth: 0 },
  rowT: { fontSize: 12.5, color: 'var(--aj-text)', lineHeight: 1.3, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis' },
  rowS: { fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' },
  moreRow: { padding: '9px 0', fontSize: 11.5, color: 'var(--aj-text-muted)' },
  hiddenGroupHead: { fontSize: 12, fontWeight: 600, color: 'var(--aj-text-mid)', padding: '4px 0 2px', display: 'flex', alignItems: 'baseline', gap: 8 },
  empty: { padding: '16px 0', fontSize: 12, color: 'var(--aj-text-muted)' },

  mono: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-muted)' },
  selCount: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-muted)', whiteSpace: 'nowrap' },
  bulkState: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, flexShrink: 0, whiteSpace: 'nowrap' },
  dot: { width: 6, height: 6, borderRadius: '50%', flex: '0 0 auto' },
  btn: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', whiteSpace: 'nowrap' },
  btnPrimary: { background: '#07272D', borderColor: '#07272D', color: '#fff' },
  filterToggle: { display: 'inline-flex', border: '0.5px solid var(--aj-border)', borderRadius: 4, overflow: 'hidden' },
  filterToggleBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-elevated)', border: 'none', padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap' },
  filterToggleBtnBorder: { borderRight: '0.5px solid var(--aj-border)' },
  filterToggleBtnOn: { background: '#07272D', color: '#fff' },

  backdrop: { position: 'fixed', inset: 0, background: 'rgba(7,39,45,0.34)', zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  // min(...) not a bare px width (2026-09-01, the iPhone pass): identical on any desktop
  // viewport, but no longer wider than a phone screen. Same treatment on MilestonePicker's own
  // 560px override below.
  modal: { width: 'min(420px, 92vw)', maxHeight: '78vh', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.22)' },
  modalHead: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '14px 16px 0' },
  modalSub: { fontSize: 11.5, color: 'var(--aj-text-muted)', padding: '5px 16px 12px', lineHeight: 1.4 },
  modalList: { overflowY: 'auto', borderTop: '0.5px solid var(--aj-border)', padding: '4px 16px' },
  crewRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' },
  crewLabel: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, cursor: 'pointer', flex: '1 1 auto' },
  capBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', background: 'transparent', border: '0.5px solid var(--aj-border)', borderRadius: 20, padding: '2px 8px', cursor: 'pointer' },
  capOn: { background: 'rgba(31,181,122,0.12)', borderColor: 'rgba(31,181,122,0.4)', color: PALETTE.green },
  prioOn: { background: 'rgba(61,125,255,0.12)', borderColor: 'rgba(61,125,255,0.4)', color: PALETTE.blue },
  modalFoot: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderTop: '0.5px solid var(--aj-border)' },
}
